import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toggleActiveTab } from '../../store/statsSlice.js';
import { setEditedEvent } from '../../store/eventsSlice';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import CustomDropdown from '../../components/components/custom_list/customDropdown';
import ApiService from '../../components/api_services/apiService';
import {
    DateRangePicker,
    formatStatNumber,
} from './stats.ui';
import EventDetailsStatsSection from './stats.event-details.section';
import EventsStatsSection from './stats.events.section';
import OrganizersStatsSection from './stats.organizers.section';
import RankingsStatsSection from './stats.rankings.section';
import UsersStatsSection from './stats.users.section';
import {
    AGE_BUCKETS,
    EVENT_DETAIL_TAB,
    EVENT_GENDER_CARD_COLORS,
    EVENT_INFO_CONTENT,
    GENDER_COLORS,
    GENDER_LABELS,
    LABELS,
    MONTH_OPTIONS,
    ORGANIZER_NAME_FALLBACK,
    RANGE_OPTIONS,
    RANKING_LIST_LIMIT,
    YEAR_OPTIONS,
} from './stats.constants';
import './Stats.css';
const isCalendarRange = (range) => range === 'RANGE' || range === 'WEEK';
const clampValue = (value, min = 0) => Math.max(min, Math.round(value));

const padNumber = (value) => String(value).padStart(2, '0');
const toDateInputValue = (date) => (
    `${date.getUTCFullYear()}-${padNumber(date.getUTCMonth() + 1)}-${padNumber(date.getUTCDate())}`
);
const parseDateValue = (value) => {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const toUtcDayStamp = (date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
const isSameUtcDate = (left, right) => (
    Boolean(left && right) && toUtcDayStamp(left) === toUtcDayStamp(right)
);

const getCurrentWeekRangeValues = () => {
    const today = new Date();
    const weekDayIndex = (today.getUTCDay() + 6) % 7;
    const start = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - weekDayIndex, 0, 0, 0)
    );
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + 6, 0, 0, 0));

    return {
        start: toDateInputValue(start),
        end: toDateInputValue(end),
    };
};
const DEFAULT_WEEK_RANGE = getCurrentWeekRangeValues();

const formatDatePl = (date) => (
    `${padNumber(date.getUTCDate())}.${padNumber(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`
);

const normalizeDateRangeValues = (startValue, endValue) => {
    const parsedStart = parseDateValue(startValue);
    const parsedEnd = parseDateValue(endValue);
    const start = parsedStart || parsedEnd;
    const end = parsedEnd || parsedStart;

    if (!start || !end) {
        return {
            startDate: null,
            endDate: null,
            startValue: '',
            endValue: '',
        };
    }

    const startStamp = toUtcDayStamp(start);
    const endStamp = toUtcDayStamp(end);
    const normalizedStart = startStamp <= endStamp ? start : end;
    const normalizedEnd = startStamp <= endStamp ? end : start;

    return {
        startDate: normalizedStart,
        endDate: normalizedEnd,
        startValue: toDateInputValue(normalizedStart),
        endValue: toDateInputValue(normalizedEnd),
    };
};

const normalizeGenderValue = (value) => (
    typeof value === 'number' ? { percent: value, count: null } : (value || { percent: 0, count: 0 })
);

const normalizeGenderBreakdown = (gender) => ({
    K: normalizeGenderValue(gender?.K),
    M: normalizeGenderValue(gender?.M),
    I: normalizeGenderValue(gender?.I),
});

const scaleDistributionCounts = (items, factor) => (
    (items || []).map((item) => ({
        ...item,
        count: item?.count != null ? clampValue(item.count * factor) : item?.count,
    }))
);

const scaleGenderBreakdownCounts = (gender, factor) => (
    Object.fromEntries(
        Object.entries(gender || {}).map(([key, value]) => {
            const normalized = normalizeGenderValue(value);
            return [
                key,
                {
                    ...normalized,
                    count: normalized.count != null ? clampValue(normalized.count * factor) : normalized.count,
                },
            ];
        })
    )
);

const getGenderRatioForFilter = (genderData, selectedGender) => {
    if (selectedGender === 'ALL') return 1;
    const selectedEntry = (genderData || []).find((item) => item.key === selectedGender);
    return selectedEntry ? (selectedEntry.percent / 100) : 0;
};

const applyGenderFilterToShare = (genderData, selectedGender) => {
    if (selectedGender === 'ALL') return genderData;

    return (genderData || []).map((item) => {
        if (item.key === selectedGender) {
            return { ...item, percent: 100 };
        }

        return { ...item, percent: 0, count: 0 };
    });
};

const applyGenderFilterToSeries = (series, ratio) => (
    (series || []).map((item) => {
        if (typeof item.total === 'number' || typeof item.confirmed === 'number') {
            const total = clampValue((item.total || 0) * ratio);
            const confirmed = clampValue((item.confirmed || 0) * ratio);
            const value = total > 0 ? clampValue((confirmed / total) * 100, 0) : 0;
            return { ...item, total, confirmed, value };
        }

        if (typeof item.value === 'number') {
            return { ...item, value: clampValue(item.value * ratio) };
        }

        return item;
    })
);

const getRangeDates = (range, year, month, weekStartDate, weekEndDate) => {
    const now = new Date();
    const selectedYear = Number(year) || now.getUTCFullYear();
    const selectedMonth = Math.max(1, Math.min(12, Number(month) || 1));

    if (range === 'YEAR') {
        const from = new Date(Date.UTC(selectedYear, 0, 1, 0, 0, 0));
        const to = new Date(Date.UTC(selectedYear, 11, 31, 23, 59, 59));
        return { from, to };
    }

    if (range === 'MONTH') {
        const from = new Date(Date.UTC(selectedYear, selectedMonth - 1, 1, 0, 0, 0));
        const to = new Date(Date.UTC(selectedYear, selectedMonth, 0, 23, 59, 59));
        return { from, to };
    }

    if (isCalendarRange(range)) {
        const normalizedWeek = normalizeDateRangeValues(weekStartDate, weekEndDate);
        const fallbackWeek = normalizeDateRangeValues(DEFAULT_WEEK_RANGE.start, DEFAULT_WEEK_RANGE.end);
        const weekStart = normalizedWeek.startDate || fallbackWeek.startDate;
        const weekEnd = normalizedWeek.endDate || fallbackWeek.endDate;
        const from = new Date(Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate(), 0, 0, 0));
        const to = new Date(Date.UTC(weekEnd.getUTCFullYear(), weekEnd.getUTCMonth(), weekEnd.getUTCDate(), 23, 59, 59));
        return { from, to };
    }

    const from = new Date(Date.UTC(now.getUTCFullYear() - 4, 0, 1, 0, 0, 0));
    const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59));
    return { from, to };
};

const toApiDateOnly = (date) => toDateInputValue(date);

const getStatisticsInterval = (range, from, to) => {
    if (range === 'YEAR') return 'MONTH';
    if (range === 'MONTH') return 'DAY';
    if (!isCalendarRange(range)) return 'YEAR';

    const daysCount = getDaysCountInclusive(from, to);
    const bucketType = getBucketTypeForDaysCount(daysCount);
    if (bucketType === 'day') return 'DAY';
    if (bucketType === 'week') return 'WEEK';
    return 'MONTH';
};

const buildStatisticsQuery = (range, year, month, weekStartDate, weekEndDate) => {
    const { from, to } = getRangeDates(range, year, month, weekStartDate, weekEndDate);

    return {
        dateFrom: toApiDateOnly(from),
        dateTo: toApiDateOnly(to),
        interval: getStatisticsInterval(range, from, to),
    };
};

const findMetricValueByKeys = (source, keys = []) => {
    for (const key of keys) {
        const parsed = toSafeMetricNumber(source?.[key]);
        if (parsed != null) return parsed;
    }

    return null;
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const toUtcStartOfDay = (date) => (
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0))
);

const addUtcDays = (date, days) => (
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days, 0, 0, 0))
);

const startOfUtcWeekMonday = (date) => {
    const startOfDay = toUtcStartOfDay(date);
    const weekDayIndex = (startOfDay.getUTCDay() + 6) % 7;
    return addUtcDays(startOfDay, -weekDayIndex);
};

const startOfUtcMonth = (date) => (
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0))
);

const endOfUtcMonth = (date) => (
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 0, 0, 0))
);

const getDaysCountInclusive = (from, to) => {
    const fromStamp = toUtcStartOfDay(from).getTime();
    const toStamp = toUtcStartOfDay(to).getTime();
    return Math.max(1, Math.floor((toStamp - fromStamp) / DAY_IN_MS) + 1);
};

const getBucketTypeForDaysCount = (daysCount) => {
    if (daysCount <= 31) return 'day';
    if (daysCount <= 90) return 'week';
    return 'month';
};

const buildRangeBuckets = (from, to) => {
    const fromDay = toUtcStartOfDay(from);
    const toDay = toUtcStartOfDay(to);
    const daysCount = getDaysCountInclusive(fromDay, toDay);
    const bucketType = getBucketTypeForDaysCount(daysCount);
    const buckets = [];

    if (bucketType === 'day') {
        for (let cursor = fromDay; cursor.getTime() <= toDay.getTime(); cursor = addUtcDays(cursor, 1)) {
            buckets.push({
                from: cursor,
                to: cursor,
                label: `${padNumber(cursor.getUTCDate())}.${padNumber(cursor.getUTCMonth() + 1)}`,
            });
        }

        return { buckets, bucketType, daysCount };
    }

    if (bucketType === 'week') {
        for (
            let cursor = startOfUtcWeekMonday(fromDay);
            cursor.getTime() <= toDay.getTime();
            cursor = addUtcDays(cursor, 7)
        ) {
            const weekEnd = addUtcDays(cursor, 6);
            buckets.push({
                from: cursor,
                to: weekEnd,
                label: `${padNumber(cursor.getUTCDate())}.${padNumber(cursor.getUTCMonth() + 1)}-${padNumber(weekEnd.getUTCDate())}.${padNumber(weekEnd.getUTCMonth() + 1)}`,
            });
        }

        return { buckets, bucketType, daysCount };
    }

    const includeYearInMonthLabel = fromDay.getUTCFullYear() !== toDay.getUTCFullYear();

    for (
        let cursor = startOfUtcMonth(fromDay);
        cursor.getTime() <= toDay.getTime();
        cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1, 0, 0, 0))
    ) {
        const monthLabel = MONTH_OPTIONS[cursor.getUTCMonth()]?.label || `${cursor.getUTCMonth() + 1}`;
        buckets.push({
            from: startOfUtcMonth(cursor),
            to: endOfUtcMonth(cursor),
            label: includeYearInMonthLabel ? `${monthLabel} ${cursor.getUTCFullYear()}` : monthLabel,
        });
    }

    return { buckets, bucketType, daysCount };
};

const pickTemplateKeyForBucketType = (bucketType) => {
    if (bucketType === 'day') return 'MONTH';
    if (bucketType === 'week') return 'WEEK';
    return 'YEAR';
};

const getCustomRangeScale = (daysCount) => {
    if (daysCount <= 31) {
        return 0.55 + (daysCount / 31) * 0.45;
    }

    if (daysCount <= 90) {
        return 0.85 + ((daysCount - 31) / 59) * 0.35;
    }

    return Math.min(1.55, 1.2 + ((daysCount - 90) / 365) * 0.35);
};

const mapTemplateSeriesToBuckets = (templateSeries, buckets, scale = 1) => {
    if (!Array.isArray(templateSeries) || !templateSeries.length || !Array.isArray(buckets) || !buckets.length) {
        return [];
    }

    const lastTemplateItem = templateSeries[templateSeries.length - 1];

    return buckets.map((bucket, index) => {
        const templateIndex = Math.min(
            templateSeries.length - 1,
            Math.floor((index * templateSeries.length) / buckets.length)
        );
        const templateItem = templateSeries[templateIndex] || lastTemplateItem;

        if (typeof templateItem?.total === 'number' || typeof templateItem?.confirmed === 'number') {
            const total = Math.max(1, clampValue((templateItem.total || 0) * scale));
            const confirmed = Math.min(total, clampValue((templateItem.confirmed || 0) * scale));
            const value = total > 0 ? clampValue((confirmed / total) * 100, 0) : 0;

            return {
                label: bucket.label,
                value,
                total,
                confirmed,
            };
        }

        return {
            label: bucket.label,
            value: clampValue((templateItem?.value || 0) * scale, 0),
        };
    });
};

const scaleSeriesValues = (series, factor = 1) => (
    (series || []).map((item) => {
        if (typeof item?.total === 'number' || typeof item?.confirmed === 'number') {
            const total = Math.max(1, clampValue((item.total || 0) * factor));
            const confirmed = Math.min(total, clampValue((item.confirmed || 0) * factor));
            const value = total > 0 ? clampValue((confirmed / total) * 100, 0) : 0;
            return { ...item, total, confirmed, value };
        }

        if (typeof item?.value === 'number') {
            return { ...item, value: clampValue(item.value * factor) };
        }

        return item;
    })
);

const getSeriesForRange = (seriesSet, range, year, month, startDate, endDate) => {
    if (!seriesSet) return [];

    if (!isCalendarRange(range)) {
        const baseSeries = seriesSet[range] || seriesSet.ALL || [];
        if (range === 'YEAR') {
            const yearValue = Number(year) || 0;
            const yearFactor = 0.9 + (Math.abs(yearValue) % 5) * 0.04;
            return scaleSeriesValues(baseSeries, yearFactor);
        }

        return baseSeries;
    }

    const { from, to } = getRangeDates(range, year, month, startDate, endDate);
    const { buckets, bucketType, daysCount } = buildRangeBuckets(from, to);
    const templateKey = pickTemplateKeyForBucketType(bucketType);
    const templateSeries = seriesSet[templateKey] || seriesSet.YEAR || seriesSet.MONTH || seriesSet.WEEK || [];
    const scale = getCustomRangeScale(daysCount);

    return mapTemplateSeriesToBuckets(templateSeries, buckets, scale);
};

const toEntries = (data) => {
    if (!data) return [];

    if (Array.isArray(data)) {
        return data
            .map((item) => {
                if (!item) return null;
                if (Array.isArray(item)) {
                    return { date: item[0], value: item[1] };
                }
                if (typeof item === 'object') {
                    const date = item.date || item.label || item.day || item.key || item.time || item.period;
                    const value = item.value ?? item.count ?? item.total ?? item.amount;
                    return { date, value };
                }
                return null;
            })
            .filter(Boolean);
    }

    if (typeof data === 'object') {
        return Object.entries(data).map(([key, value]) => ({ date: key, value }));
    }

    return [];
};

const extractCollectionPayload = (response) => (
    Array.isArray(response)
        ? response
        : response?.items
            || response?.content
            || response?.data
            || response?.results
            || response?.values
            || []
);
const extractTimeSeriesPayload = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    return response?.timeSeries
        || response?.series
        || response?.data
        || response?.items
        || [];
};
const extractDemographicBreakdownPayload = (response) => (
    response?.demographicBreakdown || response?.demography || null
);
const normalizeDemographicGender = (gender) => ({
    K: toSafeMetricNumber(gender?.K ?? gender?.female ?? gender?.women) ?? 0,
    M: toSafeMetricNumber(gender?.M ?? gender?.male ?? gender?.men) ?? 0,
    I: toSafeMetricNumber(gender?.I ?? gender?.other) ?? 0,
});
const resolveGenderDemography = (gender) => {
    const safeGender = normalizeDemographicGender(gender || {});
    const values = [safeGender.K, safeGender.M, safeGender.I].map((value) => Math.max(0, Number(value) || 0));
    const total = values.reduce((sum, value) => sum + value, 0);
    const toPercent = (value) => (total > 0 ? clampValue((value / total) * 100) : 0);
    return {
        total,
        rows: {
            K: { percent: toPercent(safeGender.K), count: clampValue(safeGender.K) },
            M: { percent: toPercent(safeGender.M), count: clampValue(safeGender.M) },
            I: { percent: toPercent(safeGender.I), count: clampValue(safeGender.I) },
        },
    };
};
const normalizeDemographicAges = (ageGroups) => {
    if (!ageGroups) return [];
    const normalizedEntries = Array.isArray(ageGroups)
        ? ageGroups.map((value) => [String(value || ''), 0])
        : Object.entries(ageGroups);

    const rows = normalizedEntries
        .map(([range, value]) => ({
            range: String(range || '').replace('–', '-'),
            count: toSafeMetricNumber(value) ?? 0,
        }))
        .filter((item) => Boolean(item.range));
    const total = rows.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    return rows.map((item) => ({
        ...item,
        percent: total > 0 ? clampValue((item.count / total) * 100) : 0,
    }));
};
const normalizeDemographicsPercentMap = (distribution) => ({
    K: toSafeMetricNumber(distribution?.K ?? distribution?.female ?? distribution?.women) ?? 0,
    M: toSafeMetricNumber(distribution?.M ?? distribution?.male ?? distribution?.men) ?? 0,
    I: toSafeMetricNumber(distribution?.I ?? distribution?.other) ?? 0,
});
const mapGenderRowsFromDemographics = (demographicsPayload, fallbackTotalCount = 0) => {
    const distribution = normalizeDemographicsPercentMap(demographicsPayload?.genderDistribution || {});
    const totals = demographicsPayload?.genderTotals || {};
    const countK = clampValue(totals?.K ?? 0);
    const countM = clampValue(totals?.M ?? 0);
    const countI = clampValue(totals?.I ?? 0);
    const fromTotals = countK + countM + countI;
    const fallbackTotal = Math.max(0, clampValue(fallbackTotalCount || 0));
    const total = fromTotals > 0 ? fromTotals : fallbackTotal;
    const toCount = (percent) => (total > 0 ? clampValue((total * percent) / 100) : 0);
    const toPercent = (count) => (total > 0 ? clampValue((count / total) * 100) : 0);

    const resolvedCountK = countK > 0 ? countK : toCount(distribution.K);
    const resolvedCountM = countM > 0 ? countM : toCount(distribution.M);
    const resolvedCountI = countI > 0 ? countI : toCount(distribution.I);

    return {
        K: { percent: distribution.K > 0 ? clampValue(distribution.K) : toPercent(resolvedCountK), count: resolvedCountK },
        M: { percent: distribution.M > 0 ? clampValue(distribution.M) : toPercent(resolvedCountM), count: resolvedCountM },
        I: { percent: distribution.I > 0 ? clampValue(distribution.I) : toPercent(resolvedCountI), count: resolvedCountI },
    };
};
const mapAgeRowsFromDemographicsApi = (demographicsPayload, selectedGender) => {
    if (!demographicsPayload) return [];

    const ageTotals = demographicsPayload?.ageTotals || {};
    const ageTotalsByGender = demographicsPayload?.ageTotalsByGender || {};
    const ageDistributionByGender = demographicsPayload?.ageDistributionByGender || {};
    const selectedDistribution = selectedGender !== 'ALL'
        ? ageDistributionByGender?.[selectedGender]
        : null;
    const baseDistribution = selectedDistribution || demographicsPayload?.ageDistribution || {};
    const selectedTotals = selectedGender !== 'ALL'
        ? (ageTotalsByGender?.[selectedGender] || {})
        : ageTotals;
    const ranges = Array.from(new Set([
        ...Object.keys(baseDistribution || {}),
        ...Object.keys(selectedTotals || {}),
    ]));

    const rows = ranges
        .map((range) => ({
            range: String(range || '').replace('â€“', '-'),
            percent: clampValue(baseDistribution?.[range] ?? 0),
            count: clampValue(selectedTotals?.[range] ?? 0),
        }))
        .filter((item) => Boolean(item.range));
    const totalFromCounts = rows.reduce((sum, item) => sum + (Number(item.count) || 0), 0);

    return rows.map((item) => ({
        ...item,
        percent: item.percent > 0
            ? item.percent
            : (totalFromCounts > 0 ? clampValue((item.count / totalFromCounts) * 100) : 0),
    }));
};
const extractEventImageHash = (source, includeLegacyImageField = false) => {
    if (!source) return null;

    const candidates = [
        source?.image_hash_code,
        source?.imageHashCode,
        source?.imageId,
        source?.imageUrl,
        source?.image_url,
    ];

    if (includeLegacyImageField) {
        candidates.push(source?.image);
    }

    let firstNonEmpty = null;
    for (const value of candidates) {
        if (value == null) continue;
        const asString = String(value);
        if (!asString) continue;
        if (!firstNonEmpty) {
            firstNonEmpty = asString;
        }
        if (asString.startsWith('_EVENT_')) {
            return asString;
        }
    }

    return firstNonEmpty;
};

const mapUnifiedRankingItems = (response, metricType) => {
    const metricTypeUpper = String(metricType || '').toUpperCase();
    const metricField = metricTypeUpper === 'JOINS' || metricTypeUpper === 'PACKAGES' ? 'packs' : 'likes';
    const items = extractCollectionPayload(response);

    return (Array.isArray(items) ? items : [])
        .map((item, index) => {
            const locationValue = item?.location || '';
            const [place, city] = String(locationValue).split(',').map((part) => part?.trim()).filter(Boolean);
            const metricValue = pickFirstMetricNumber(item?.metricValue, item?.value, item?.counting, item?.count, item?.total);
            const imageHashCode = extractEventImageHash(item);

            return {
                id: item?.eventId ?? item?.id ?? index + 1,
                name: item?.title ?? item?.name ?? 'Wydarzenie',
                place: place || item?.place || '',
                city: city || item?.city || '',
                date: formatRankingDate(item?.eventDate || item?.date || item?.startDate),
                rank: Number(item?.rank) || index + 1,
                likes: metricField === 'likes' ? (metricValue ?? 0) : 0,
                packs: metricField === 'packs' ? (metricValue ?? 0) : 0,
                metricValue: metricValue ?? 0,
                imageHashCode,
            };
        });
};

const getLabelForDate = (date, range) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

    if (range === 'ALL') {
        return `${date.getUTCFullYear()}`;
    }

    if (range === 'YEAR') {
        return LABELS.YEAR[date.getUTCMonth()];
    }

    if (range === 'MONTH') {
        return `${date.getUTCDate()}`;
    }

    const weekDayIndex = (date.getUTCDay() + 6) % 7;
    return LABELS.WEEK[weekDayIndex];
};

const buildSeriesFromResponse = (data, range, options = {}) => {
    const entries = toEntries(data);

    if (isCalendarRange(range)) {
        const { year, month, startValue, endValue } = options;
        const { from, to } = getRangeDates(range, year, month, startValue, endValue);
        const { buckets, bucketType } = buildRangeBuckets(from, to);
        const sumsByBucket = new Map(
            buckets.map((bucket) => [toDateInputValue(bucket.from), 0])
        );

        entries.forEach(({ date, value }) => {
            const parsedDate = new Date(date);
            if (Number.isNaN(parsedDate.getTime())) return;

            const valueNumber = Number(value) || 0;
            const utcDate = toUtcStartOfDay(parsedDate);
            const bucketDate =
                bucketType === 'day'
                    ? utcDate
                    : bucketType === 'week'
                        ? startOfUtcWeekMonday(utcDate)
                        : startOfUtcMonth(utcDate);
            const bucketKey = toDateInputValue(bucketDate);

            if (!sumsByBucket.has(bucketKey)) return;
            sumsByBucket.set(bucketKey, (sumsByBucket.get(bucketKey) || 0) + valueNumber);
        });

        return buckets.map((bucket) => ({
            label: bucket.label,
            value: clampValue(sumsByBucket.get(toDateInputValue(bucket.from)) || 0),
        }));
    }

    const labels = LABELS[range];
    if (!labels) return [];

    const buckets = labels.reduce((acc, label) => {
        acc[label] = 0;
        return acc;
    }, {});

    entries.forEach(({ date, value }) => {
        const parsedDate = new Date(date);
        const label = getLabelForDate(parsedDate, range);
        if (!label || !(label in buckets)) return;
        buckets[label] += Number(value) || 0;
    });

    return labels.map((label) => ({ label, value: clampValue(buckets[label]) }));
};

const formatRankingDate = (dateString) => {
    if (!dateString) return '-';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return dateString;
    return `${padNumber(parsed.getDate())}.${padNumber(parsed.getMonth() + 1)}.${parsed.getFullYear()}`;
};

const toSafeMetricNumber = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(0, parsed);
};

const pickFirstMetricNumber = (...values) => {
    for (const value of values) {
        const parsed = toSafeMetricNumber(value);
        if (parsed != null) return parsed;
    }

    return null;
};

const normalizeTextValue = (value) => String(value ?? '').trim().toLowerCase();
const pickFirstDefined = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null) return value;
    }
    return null;
};
const normalizeOwnerIdKey = (value) => {
    const raw = String(value ?? '').trim();
    return raw || null;
};
const resolveOwnerIdFromEventLike = (eventLike = {}) => (
    normalizeOwnerIdKey(
        eventLike?.ownerId
        ?? eventLike?.owner_id
        ?? eventLike?.ownerUUID
        ?? eventLike?.owner_uuid
        ?? eventLike?.owner?.id
        ?? eventLike?.owner?.ownerId
        ?? eventLike?.owner?.owner_id
        ?? eventLike?.owner?.userId
        ?? eventLike?.owner?.user_id
        ?? eventLike?.userId
        ?? eventLike?.user_id
        ?? eventLike?.organizerId
        ?? eventLike?.organizer_id
        ?? eventLike?.organiserId
        ?? eventLike?.organiser_id
        ?? eventLike?.event?.ownerId
        ?? eventLike?.event?.owner_id
        ?? eventLike?.event?.owner?.id
    )
);
const normalizeEventIdKey = (value) => {
    const raw = String(value ?? '').trim();
    return raw || null;
};
const hasOrganizerName = (value) => {
    const normalized = normalizeTextValue(value);
    return Boolean(normalized) && normalized !== normalizeTextValue(ORGANIZER_NAME_FALLBACK);
};
const isUuidLike = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value ?? ''));
const pickRawImageId = (...values) => {
    for (const value of values) {
        if (value == null) continue;
        const asString = String(value);
        if (!asString) continue;
        return asString;
    }

    return null;
};
const hasEventImagePrefix = (value) => String(value ?? '').startsWith('_EVENT_');

const resolveNumericCategoryId = (...values) => {
    for (const value of values) {
        const parsed = Number(value);
        if (Number.isInteger(parsed) && parsed >= 0) {
            return String(parsed);
        }
    }

    return null;
};

const USERS_SUMMARY = [
    { key: 'registered', label: 'Zarejestrowani', value: '0', note: 'stan na dziś' },
    { key: 'new', label: 'Nowi', value: '0', note: 'w wybranym okresie' },
    { key: 'active', label: 'Aktywni', value: '0', note: 'w wybranym okresie' },
    { key: 'inactive', label: 'Niezalogowani', value: '0', note: 'w wybranym okresie' },
    { key: 'pack', label: 'Aktywność paczek', value: '0', note: 'dołączenie lub utworzenie' },
    { key: 'confirmed', label: 'Potwierdzone wyjścia', value: '0%', note: '0 / 0 wyjść' },
];

const USERS_PAIRED_EXTRA_SUMMARY = [
    { key: 'accountDeletion', label: 'Usunięcia konta', value: '0', note: 'w wybranym okresie' },
    { key: 'reports', label: 'Zgłoszenia', value: '0', note: 'w wybranym okresie' },
];

const ORGANIZERS_SUMMARY = [
    { key: 'registered', label: 'Zarejestrowani', value: '0', note: 'stan na dziś' },
    { key: 'new', label: 'Nowi organizatorzy', value: '0', note: 'w wybranym okresie' },
];

const EVENTS_SUMMARY_BASE = {
    all: 2140,
    festival: 560,
    sport: 430,
    culture: 380,
};
const EVENTS_ACTIVE_TOTAL_FACTOR = 0.42;

const CITY_DATA_BY_FILTER = {
    ALL: [
        { city: 'Warszawa', percent: 24, count: 1220 },
        { city: 'Kraków', percent: 18, count: 910 },
        { city: 'Wrocław', percent: 12, count: 610 },
        { city: 'Poznań', percent: 10, count: 520 },
        { city: 'Gdańsk', percent: 9, count: 480 },
    ],
    K: [
        { city: 'Warszawa', percent: 22, count: 710 },
        { city: 'Kraków', percent: 19, count: 610 },
        { city: 'Wrocław', percent: 13, count: 420 },
        { city: 'Poznań', percent: 11, count: 350 },
        { city: 'Gdańsk', percent: 9, count: 290 },
    ],
    M: [
        { city: 'Warszawa', percent: 26, count: 760 },
        { city: 'Kraków', percent: 17, count: 500 },
        { city: 'Wrocław', percent: 11, count: 320 },
        { city: 'Poznań', percent: 9, count: 270 },
        { city: 'Gdańsk', percent: 8, count: 230 },
    ],
    I: [
        { city: 'Warszawa', percent: 20, count: 115 },
        { city: 'Kraków', percent: 16, count: 92 },
        { city: 'Wrocław', percent: 14, count: 81 },
        { city: 'Poznań', percent: 12, count: 69 },
        { city: 'Gdańsk', percent: 10, count: 58 },
    ],
};

const AGE_BY_GENDER = {
    ALL: [
        { range: AGE_BUCKETS[0], percent: 26, count: 2402 },
        { range: AGE_BUCKETS[1], percent: 17, count: 1571 },
        { range: AGE_BUCKETS[2], percent: 28, count: 2587 },
        { range: AGE_BUCKETS[3], percent: 19, count: 1756 },
        { range: AGE_BUCKETS[4], percent: 10, count: 925 },
    ],
    K: [
        { range: AGE_BUCKETS[0], percent: 28, count: 680 },
        { range: AGE_BUCKETS[1], percent: 16, count: 390 },
        { range: AGE_BUCKETS[2], percent: 26, count: 640 },
        { range: AGE_BUCKETS[3], percent: 18, count: 440 },
        { range: AGE_BUCKETS[4], percent: 12, count: 290 },
    ],
    M: [
        { range: AGE_BUCKETS[0], percent: 22, count: 520 },
        { range: AGE_BUCKETS[1], percent: 18, count: 430 },
        { range: AGE_BUCKETS[2], percent: 30, count: 720 },
        { range: AGE_BUCKETS[3], percent: 20, count: 480 },
        { range: AGE_BUCKETS[4], percent: 10, count: 240 },
    ],
    I: [
        { range: AGE_BUCKETS[0], percent: 30, count: 160 },
        { range: AGE_BUCKETS[1], percent: 20, count: 105 },
        { range: AGE_BUCKETS[2], percent: 25, count: 130 },
        { range: AGE_BUCKETS[3], percent: 15, count: 78 },
        { range: AGE_BUCKETS[4], percent: 10, count: 52 },
    ],
};

const USERS_METRIC_DEMOGRAPHY = {
    newUsers: {
        gender: {
            K: { percent: 58, count: 1320 },
            M: { percent: 42, count: 960 },
        },
        ages: AGE_BY_GENDER.K,
    },
    activeUsers: {
        gender: {
            K: { percent: 54, count: 2480 },
            M: { percent: 46, count: 2110 },
        },
        ages: AGE_BY_GENDER.M,
    },
    packActivity: {
        gender: {
            K: { percent: 49, count: 1100 },
            M: { percent: 51, count: 1140 },
        },
        ages: AGE_BY_GENDER.K,
    },
};

const ORGANIZERS_CATEGORY_SHARE = [
    { category: 'BAR & FUN', percent: 14, count: 11 },
    { category: 'SZTUKA I KULTURA', percent: 16, count: 13 },
    { category: 'TEATR I KINO', percent: 9, count: 7 },
    { category: 'KULINARNE', percent: 8, count: 6 },
    { category: 'FESTIWALE & EVENTY', percent: 19, count: 15 },
    { category: 'MUZYKA', percent: 15, count: 12 },
    { category: 'WARSZTATY', percent: 11, count: 9 },
    { category: 'AKTYWNOŚĆ', percent: 8, count: 6 },
];

const getTrendLastValue = (series) => {
    if (!Array.isArray(series) || !series.length) return 0;
    const lastPoint = series[series.length - 1];
    return clampValue(lastPoint?.value || 0);
};


const buildCountsFromRatios = (total, ratios) => {
    const safeTotal = Math.max(0, Math.round(Number(total) || 0));
    if (!safeTotal) return ratios.map(() => 0);

    const weighted = ratios.map((ratio) => safeTotal * ratio);
    const base = weighted.map((value) => Math.floor(value));
    let remainder = safeTotal - base.reduce((sum, value) => sum + value, 0);
    const order = weighted
        .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
        .sort((left, right) => right.fraction - left.fraction);

    for (let index = 0; index < remainder; index += 1) {
        const next = order[index % order.length];
        base[next.index] += 1;
    }

    return base;
};

const addPercentToLegendItems = (items) => {
    const total = items.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
    return items.map((item) => ({
        ...item,
        percent: total > 0 ? clampValue(((Number(item?.count) || 0) / total) * 100) : 0,
    }));
};

const mapStatsEventToDetailPanelEvent = (eventLike = {}) => ({
    id: pickFirstDefined(eventLike?.id, eventLike?.eventId, eventLike?.event_id, eventLike?.uuid, eventLike?.eventUuid),
    name: pickFirstDefined(eventLike?.name, eventLike?.title, eventLike?.eventName, eventLike?.event_name, eventLike?.eventTitle) || 'Wydarzenie',
    organizerName: pickFirstDefined(
        eventLike?.organizerName,
        eventLike?.organizer,
        eventLike?.companyName,
        eventLike?.company_name,
        eventLike?.organiserName,
    ) || ORGANIZER_NAME_FALLBACK,
    ownerId: resolveOwnerIdFromEventLike(eventLike) || resolveOwnerIdFromEventLike(eventLike?.event),
    city: pickFirstDefined(eventLike?.city, eventLike?.cityName, eventLike?.city_name) || '',
    place: pickFirstDefined(eventLike?.place, eventLike?.placeName, eventLike?.place_name) || '',
    adres: pickFirstDefined(eventLike?.adres, eventLike?.address) || '',
    zipCode: pickFirstDefined(eventLike?.zipCode, eventLike?.zip_code, eventLike?.postalCode, eventLike?.postal_code) || '',
    startDate: pickFirstDefined(eventLike?.startDate, eventLike?.start_date, eventLike?.eventDate, eventLike?.event_date, eventLike?.date) || null,
    endDate: pickFirstDefined(eventLike?.endDate, eventLike?.end_date) || null,
    categoryId: pickFirstDefined(eventLike?.categoryId, eventLike?.category_id, eventLike?.eventsCategoryId),
    categoryName: pickFirstDefined(eventLike?.categoryName, eventLike?.category_name, eventLike?.eventsCategoryName) || '',
    ticketInfo: pickFirstDefined(eventLike?.ticketInfo, eventLike?.ticket_info) || '',
    ticketPrice: pickFirstDefined(eventLike?.ticketPrice, eventLike?.ticket_price) || '',
    description: pickFirstDefined(eventLike?.description, eventLike?.eventDescription) || '',
    isActive: pickFirstDefined(eventLike?.isActive, eventLike?.is_active, eventLike?.active) ?? true,
    image_hash_code: pickFirstDefined(eventLike?.image_hash_code, eventLike?.imageHashCode, eventLike?.imageUrl, eventLike?.image_url) || '',
    added_on_utc: pickFirstDefined(eventLike?.added_on_utc, eventLike?.addedOnUtc, eventLike?.createdAt, eventLike?.created_at) || null,
    status: pickFirstDefined(eventLike?.status, eventLike?.eventStatus) || 'accepted',
});

const mapStatsEventToSidebarItem = (eventLike = {}, index = 0) => {
    const source = eventLike?.event || eventLike?.data || eventLike;
    const resolvedId = normalizeEventIdKey(
        pickFirstDefined(source?.id, source?.eventId, source?.event_id, source?.uuid, source?.eventUuid)
    );
    if (!resolvedId) return null;

    const rawLocation = String(pickFirstDefined(source?.location, source?.eventLocation) || '').trim();
    const [locationPlace, locationCity] = rawLocation
        ? rawLocation.split(',').map((part) => String(part || '').trim())
        : ['', ''];
    const startDate = pickFirstDefined(
        source?.startDate,
        source?.start_date,
        source?.eventDate,
        source?.event_date,
        source?.date,
        source?.meetingDate
    ) || null;
    const endDate = pickFirstDefined(source?.endDate, source?.end_date) || null;
    const city = pickFirstDefined(source?.city, source?.cityName, source?.city_name, locationCity) || '';
    const place = pickFirstDefined(source?.place, source?.placeName, source?.place_name, locationPlace) || '';

    return {
        id: resolvedId,
        rank: Number(source?.rank) || index + 1,
        name: pickFirstDefined(source?.name, source?.title, source?.eventName, source?.event_name, source?.eventTitle) || 'Wydarzenie',
        organizerName: pickFirstDefined(
            source?.organizerName,
            source?.organizer,
            source?.companyName,
            source?.company_name,
            source?.organiserName
        ) || ORGANIZER_NAME_FALLBACK,
        ownerId: resolveOwnerIdFromEventLike(source) || resolveOwnerIdFromEventLike(eventLike),
        city,
        place,
        categoryId: pickFirstDefined(source?.categoryId, source?.category_id, source?.eventsCategoryId, source?.eventCategoryId),
        categoryName: pickFirstDefined(source?.categoryName, source?.category_name, source?.eventsCategoryName, source?.eventCategoryName) || '',
        startDate,
        endDate,
        imageUrl: pickFirstDefined(source?.imageUrl, source?.image_url, source?.imageHashCode, source?.image_hash_code, source?.image) || '',
        imageHashCode: extractEventImageHash(source, true),
        statsPayload: eventLike,
    };
};

const upsertStatsEvent = (items, nextItem) => {
    if (!nextItem?.id) return items;
    const nextId = normalizeEventIdKey(nextItem.id);
    const existingIndex = items.findIndex((item) => normalizeEventIdKey(item.id) === nextId);
    if (existingIndex === -1) {
        return [nextItem, ...items];
    }

    const updated = [...items];
    const existingItem = updated[existingIndex];
    const existingHash = pickRawImageId(existingItem?.imageHashCode);
    const nextHash = pickRawImageId(nextItem?.imageHashCode);

    let mergedHash = nextHash ?? existingHash ?? null;
    if (existingHash && nextHash) {
        const existingHasPrefix = hasEventImagePrefix(existingHash);
        const nextHasPrefix = hasEventImagePrefix(nextHash);
        if (existingHasPrefix && !nextHasPrefix) {
            mergedHash = existingHash;
        }
    }

    updated[existingIndex] = {
        ...existingItem,
        ...nextItem,
        imageHashCode: mergedHash,
    };
    return updated;
};

const parseStatsEventDateValue = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;

    const parsedNative = new Date(raw);
    if (!Number.isNaN(parsedNative.getTime())) return parsedNative;

    const polishDateMatch = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!polishDateMatch) return null;

    const [, day, month, year, hours = '0', minutes = '0', seconds = '0'] = polishDateMatch;
    const parsedPolish = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds),
    );
    return Number.isNaN(parsedPolish.getTime()) ? null : parsedPolish;
};

const formatSidebarEventDate = (eventItem) => {
    const parsed = parseStatsEventDateValue(eventItem?.startDate || eventItem?.date || '');
    if (!parsed) return '-';
    return `${padNumber(parsed.getDate())}.${padNumber(parsed.getMonth() + 1)}.${parsed.getFullYear()}`;
};

const formatSidebarEventDateLabel = (eventItem) => {
    const parsed = parseStatsEventDateValue(eventItem?.startDate || eventItem?.date || '');
    if (!parsed) return '-';

    const raw = new Intl.DateTimeFormat('pl-PL', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(parsed);

    const withoutWeekdayDot = raw.replace(/\./g, '');
    return withoutWeekdayDot.charAt(0).toUpperCase() + withoutWeekdayDot.slice(1);
};

const formatSidebarEventTimeLabel = (eventItem) => {
    const parsed = parseStatsEventDateValue(eventItem?.startDate || eventItem?.date || '');
    if (!parsed) return '-';

    return `${padNumber(parsed.getHours())}:${padNumber(parsed.getMinutes())}`;
};

function Stats() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const activeTab = useSelector((state) => state.stats.activeTab);
    const accessToken = useSelector((state) => state.tokens.accessToken);

    const [usersRange, setUsersRange] = useState('ALL');
    const [usersYear, setUsersYear] = useState('2026');
    const [usersMonth, setUsersMonth] = useState('01');
    const [usersWeekStartDate, setUsersWeekStartDate] = useState(DEFAULT_WEEK_RANGE.start);
    const [usersWeekEndDate, setUsersWeekEndDate] = useState(DEFAULT_WEEK_RANGE.end);
    const [usersGender, setUsersGender] = useState('ALL');

    const [organizersRange, setOrganizersRange] = useState('ALL');
    const [b2bPromoSummary, setB2bPromoSummary] = useState(null);
    const [organizersYear, setOrganizersYear] = useState('2026');
    const [organizersMonth, setOrganizersMonth] = useState('01');
    const [organizersWeekStartDate, setOrganizersWeekStartDate] = useState(DEFAULT_WEEK_RANGE.start);
    const [organizersWeekEndDate, setOrganizersWeekEndDate] = useState(DEFAULT_WEEK_RANGE.end);

    const [eventsRange, setEventsRange] = useState('ALL');
    const [eventsYear, setEventsYear] = useState('2026');
    const [eventsMonth, setEventsMonth] = useState('01');
    const [eventsWeekStartDate, setEventsWeekStartDate] = useState(DEFAULT_WEEK_RANGE.start);
    const [eventsWeekEndDate, setEventsWeekEndDate] = useState(DEFAULT_WEEK_RANGE.end);
    const [eventsSummaryMode, setEventsSummaryMode] = useState('ALL');
    const [eventsMetric, setEventsMetric] = useState('views');
    const [eventsCategory, setEventsCategory] = useState('0');
    const [rankingRange, setRankingRange] = useState('ALL_TIME');
    const [rankingCategory, setRankingCategory] = useState('0');
    const [rankingCategories, setRankingCategories] = useState([{ value: '0', label: 'Wszystkie', apiValue: '0' }]);
    const [rankingVisibleList, setRankingVisibleList] = useState('interest');
    const [rankingInfoModalOpen, setRankingInfoModalOpen] = useState(false);
    const [rankingInterest, setRankingInterest] = useState([]);
    const [rankingPacks, setRankingPacks] = useState([]);
    const [eventsTopInterestApi, setEventsTopInterestApi] = useState(null);
    const [eventsTopPacksApi, setEventsTopPacksApi] = useState(null);
    const [eventsTopInterestExpanded, setEventsTopInterestExpanded] = useState(false);
    const [eventsTopPacksExpanded, setEventsTopPacksExpanded] = useState(false);
    const [packSeriesApi, setPackSeriesApi] = useState(null);
    const [usersSeriesApi, setUsersSeriesApi] = useState({});
    const [usersSummaryApi, setUsersSummaryApi] = useState(null);
    const [usersDemographicsApi, setUsersDemographicsApi] = useState(null);
    const [usersTopCitiesApi, setUsersTopCitiesApi] = useState(null);
    const [organizersSeriesApi, setOrganizersSeriesApi] = useState({});
    const [organizersSummaryApi, setOrganizersSummaryApi] = useState(null);
    const [eventsSummaryApi, setEventsSummaryApi] = useState(null);
    const [eventsAvgParticipantsSeriesApi, setEventsAvgParticipantsSeriesApi] = useState(null);
    const [eventStatsSearch, setEventStatsSearch] = useState('');
    const [eventStatsList, setEventStatsList] = useState([]);
    const [eventStatsListLoading, setEventStatsListLoading] = useState(false);
    const [eventStatsListError, setEventStatsListError] = useState('');
    const [selectedEventStatsId, setSelectedEventStatsId] = useState(null);
    const [selectedEventStats, setSelectedEventStats] = useState(null);
    const [eventViewingSeriesApi, setEventViewingSeriesApi] = useState(null);
    const [eventPackSeriesApi, setEventPackSeriesApi] = useState(null);
    const [eventActivitySeriesApi, setEventActivitySeriesApi] = useState(null);
    const [eventSummaryApi, setEventSummaryApi] = useState(null);
    const [eventAgeDataApi, setEventAgeDataApi] = useState(null);
    const [eventImageUrlById, setEventImageUrlById] = useState({});
    const [eventAgeFilter, setEventAgeFilter] = useState('ALL');
    const [eventVisibleSeries, setEventVisibleSeries] = useState({
        viewing: true,
        packs: true,
        favorites: true,
    });
    const [eventInfoModalKey, setEventInfoModalKey] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const eventInfoModalContent = EVENT_INFO_CONTENT[eventInfoModalKey] || null;
    const fetchedImageHashesByEventRef = useRef(new Map());
    const objectUrlByEventRef = useRef(new Map());
    const imageFetchInFlightByEventRef = useRef(new Set());
    const organizerNameByOwnerIdRef = useRef(new Map());
    const organizerNameFetchInFlightRef = useRef(new Set());

    const patchOrganizerNameByOwnerId = useCallback((ownerId, organizerName) => {
        const ownerIdKey = normalizeOwnerIdKey(ownerId);
        if (!ownerIdKey) return;

        const resolvedOrganizerName = String(organizerName || '').trim() || ORGANIZER_NAME_FALLBACK;
        organizerNameByOwnerIdRef.current.set(ownerIdKey, resolvedOrganizerName);

        setEventStatsList((previous) => previous.map((item) => (
            (
                (resolveOwnerIdFromEventLike(item) || resolveOwnerIdFromEventLike(item?.statsPayload)) === ownerIdKey
                && !hasOrganizerName(item?.organizerName)
            )
                ? { ...item, ownerId: ownerIdKey, organizerName: resolvedOrganizerName }
                : item
        )));

        setSelectedEventStats((previous) => (
            previous
            && (resolveOwnerIdFromEventLike(previous) || resolveOwnerIdFromEventLike(previous?.statsPayload)) === ownerIdKey
            && !hasOrganizerName(previous?.organizerName)
                ? { ...previous, ownerId: ownerIdKey, organizerName: resolvedOrganizerName }
                : previous
        ));
    }, []);

    const fetchOrganizerCompanyNameByOwnerId = useCallback(async (ownerId) => {
        const ownerIdKey = normalizeOwnerIdKey(ownerId);
        if (!ownerIdKey || !accessToken) return ORGANIZER_NAME_FALLBACK;

        try {
            const response = await fetch(`/api/organiser/profiles/${ownerIdKey}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                credentials: 'include',
                mode: 'cors',
            });

            if (response.status === 404) return ORGANIZER_NAME_FALLBACK;
            if (!response.ok) return ORGANIZER_NAME_FALLBACK;

            const payload = await response.json().catch(() => null);
            return String(payload?.companyName || '').trim() || ORGANIZER_NAME_FALLBACK;
        } catch (error) {
            return ORGANIZER_NAME_FALLBACK;
        }
    }, [accessToken]);
    const ensureOrganizerNameForEventItem = useCallback((eventItem) => {
        if (!eventItem) return eventItem;

        const ownerIdKey = resolveOwnerIdFromEventLike(eventItem) || resolveOwnerIdFromEventLike(eventItem?.statsPayload);
        if (!ownerIdKey || hasOrganizerName(eventItem?.organizerName)) {
            return eventItem;
        }

        const cachedOrganizerName = organizerNameByOwnerIdRef.current.get(ownerIdKey);
        if (cachedOrganizerName) {
            return {
                ...eventItem,
                ownerId: ownerIdKey,
                organizerName: cachedOrganizerName,
            };
        }

        if (!organizerNameFetchInFlightRef.current.has(ownerIdKey)) {
            organizerNameFetchInFlightRef.current.add(ownerIdKey);
            fetchOrganizerCompanyNameByOwnerId(ownerIdKey)
                .then((companyName) => {
                    patchOrganizerNameByOwnerId(ownerIdKey, companyName);
                })
                .finally(() => {
                    organizerNameFetchInFlightRef.current.delete(ownerIdKey);
                });
        }

        return eventItem;
    }, [fetchOrganizerCompanyNameByOwnerId, patchOrganizerNameByOwnerId]);

    useEffect(() => {
        ApiService.getB2bStatisticsSummary()
            .then((data) => setB2bPromoSummary(data))
            .catch(() => setB2bPromoSummary(null));
    }, []);

    const handleSidebarClick = (tabName) => {
        dispatch(toggleActiveTab(tabName));
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            setRefreshTick((previous) => previous + 1);
        }, 10000);

        return () => {
            clearInterval(intervalId);
        };
    }, []);
    const handleEventVisibleSeriesToggle = useCallback((seriesKey) => {
        setEventVisibleSeries((previous) => {
            const activeCount = Object.values(previous).filter(Boolean).length;
            if (previous[seriesKey] && activeCount === 1) return previous;

            return {
                ...previous,
                [seriesKey]: !previous[seriesKey],
            };
        });
    }, []);
    const handleEventTimeScopeChange = useCallback((event) => {
        const nextValue = event.target.value;
        if (nextValue === 'ALL') {
            setEventsRange('ALL');
            return;
        }

        const currentWeekRange = getCurrentWeekRangeValues();
        setEventsRange('WEEK');
        setEventsWeekStartDate(currentWeekRange.start);
        setEventsWeekEndDate(currentWeekRange.end);
    }, []);

    const requestedEventContext = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const view = searchParams.get('view');
        const parsedEventId = normalizeEventIdKey(searchParams.get('eventId'));

        return {
            isEventView: view === 'event',
            eventId: parsedEventId,
        };
    }, [location.search]);

    const handleSelectEventStats = useCallback((eventItem) => {
        const eventId = normalizeEventIdKey(eventItem?.id);
        if (!eventId) return;

        const resolvedEventItem = ensureOrganizerNameForEventItem(eventItem);

        setSelectedEventStatsId(eventId);
        setSelectedEventStats(resolvedEventItem);
        dispatch(toggleActiveTab(EVENT_DETAIL_TAB));
        navigate(`/statistics?view=event&eventId=${eventId}`, {
            replace: true,
            state: { eventFromList: resolvedEventItem },
        });
    }, [dispatch, navigate, ensureOrganizerNameForEventItem]);

    const categoryOptionsByValue = useMemo(
        () => new Map((rankingCategories || []).map((item) => [String(item.value), item])),
        [rankingCategories]
    );
    const resolveCategoryApiValue = useCallback((selectedValue) => {
        if (selectedValue == null) return '0';

        const option = categoryOptionsByValue.get(String(selectedValue));
        const numericFromOption = resolveNumericCategoryId(option?.apiValue, option?.value);
        if (numericFromOption) return numericFromOption;

        return resolveNumericCategoryId(selectedValue) || '0';
    }, [categoryOptionsByValue]);

    const renderRangeFilters = ({
        range,
        setRange,
        year,
        setYear,
        month,
        weekStartDate,
        setWeekStartDate,
        weekEndDate,
        setWeekEndDate,
    }) => {
        const dateRange = isCalendarRange(range)
            ? getRangeDates(range, year, month, weekStartDate, weekEndDate)
            : null;

        return (
            <div className="stats-filter-group">
                <CustomDropdown
                    placeholder="Zakres"
                    options={RANGE_OPTIONS}
                    value={range}
                    onChange={(event) => setRange(event.target.value)}
                    hoverClass="hover-active"
                    activeClass="is-active"
                />
                {range === 'YEAR' && (
                    <CustomDropdown
                        placeholder="Rok"
                        options={YEAR_OPTIONS}
                        value={year}
                        onChange={(event) => setYear(event.target.value)}
                        hoverClass="hover-active"
                        activeClass="is-active"
                    />
                )}
                {isCalendarRange(range) && (
                    <>
                        <DateRangePicker
                            startValue={weekStartDate}
                            endValue={weekEndDate}
                            monthOptions={MONTH_OPTIONS}
                            parseDateValue={parseDateValue}
                            normalizeDateRangeValues={normalizeDateRangeValues}
                            formatDatePl={formatDatePl}
                            toDateInputValue={toDateInputValue}
                            toUtcDayStamp={toUtcDayStamp}
                            isSameUtcDate={isSameUtcDate}
                            onChange={(nextStartDate, nextEndDate) => {
                                setWeekStartDate(nextStartDate);
                                setWeekEndDate(nextEndDate);
                            }}
                        />
                        <div className="stats-week-range-caption">
                            Zakres: {formatDatePl(dateRange.from)} - {formatDatePl(dateRange.to)}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const mapRankingData = useCallback((data, options = {}) => {
        const { limit = RANKING_LIST_LIMIT } = options;
        const list = Array.isArray(data) ? data : data?.data || [];
        if (!list.length) return [];

        const mapped = list.map((item, index) => {
            const locationValue = String(item?.location || '').trim();
            const [locationPlace, locationCity] = locationValue
                ? locationValue.split(',').map((part) => String(part || '').trim())
                : ['', ''];
            const views = pickFirstMetricNumber(
                item?.views,
                item?.viewCount,
                item?.viewingCount,
                item?.totalViews
            );
            const likes = pickFirstMetricNumber(item?.likes, item?.likesCount, item?.likeCount);
            const packs = pickFirstMetricNumber(
                item?.bunchCount,
                item?.bunchesCount,
                item?.createdBunches,
                item?.packs,
                item?.packCount
            );
            const fallbackValue = pickFirstMetricNumber(item?.counting, item?.count, item?.value, views, packs);
            const resolvedViews = views ?? fallbackValue ?? 0;
            const rawCategoryId = item?.categoryId ?? item?.eventsCategoryId ?? item?.catId ?? item?.category?.id ?? item?.idCategory;
            const rawCategoryName = item?.categoryName ?? item?.eventsCategoryName ?? item?.category?.name ?? item?.catName;
            const imageHashCode = extractEventImageHash(item);

            return {
                id: item?.id ?? item?.eventId ?? index + 1,
                categoryId: String(rawCategoryId ?? 0),
                categoryName: rawCategoryName ? String(rawCategoryName) : '',
                name: item?.title ?? item?.name ?? item?.eventName ?? 'Wydarzenie',
                city: (item?.city ?? item?.eventCity ?? locationCity) || '',
                place: (item?.place ?? item?.eventPlace ?? locationPlace) || '',
                date: formatRankingDate(item?.startDate || item?.date || item?.meetingDate || item?.added_on_utc),
                rank: index + 1,
                value: fallbackValue ?? 0,
                views: resolvedViews,
                likes: likes ?? clampValue(resolvedViews * 0.03),
                packs: packs ?? clampValue(resolvedViews * 0.02),
                imageHashCode,
            };
        });

        return typeof limit === 'number' ? mapped.slice(0, limit) : mapped;
    }, []);

    const handleOpenEventFromStats = useCallback(async (eventItem) => {
        const eventId = normalizeEventIdKey(eventItem?.id ?? eventItem?.eventId);
        if (!eventId) {
            return;
        }

        navigate('/events');

        try {
            const fullEventData = mapStatsEventToDetailPanelEvent({
                ...(eventItem || {}),
                id: eventId,
                eventId,
            });
            dispatch(setEditedEvent(fullEventData));
        } catch (error) {
            console.error(`Błąd pobierania szczegółów wydarzenia ${eventId}:`, error);
        }
    }, [dispatch, navigate]);

    const fetchRankings = useCallback(async () => {
        try {
            const rankingCategoryApiValue = resolveCategoryApiValue(rankingCategory);
            const [interestResponse, packsResponse] = await Promise.all([
                ApiService.getStatisticsRankingsEvents({
                    metric: 'LIKES',
                    timeRange: rankingRange,
                    limit: RANKING_LIST_LIMIT,
                    ...(rankingCategoryApiValue !== '0' && { category: rankingCategoryApiValue }),
                }),
                ApiService.getStatisticsRankingsEvents({
                    metric: 'JOINS',
                    timeRange: rankingRange,
                    limit: RANKING_LIST_LIMIT,
                    ...(rankingCategoryApiValue !== '0' && { category: rankingCategoryApiValue }),
                }),
            ]);

            const interestMapped = mapUnifiedRankingItems(interestResponse, 'LIKES');
            const packsMapped = mapUnifiedRankingItems(packsResponse, 'JOINS');

            setRankingInterest(interestMapped);
            setRankingPacks(packsMapped);
        } catch (error) {
            console.error('Błąd pobierania rankingów:', error);
            setRankingInterest([]);
            setRankingPacks([]);
        }
    }, [rankingRange, rankingCategory, resolveCategoryApiValue]);

    useEffect(() => {
        let isMounted = true;

        ApiService.getEventsCategories()
            .then((response) => {
                const data = response?.data || response || [];
                if (!Array.isArray(data) || !isMounted) return;

                const mappedCategories = data.map((item, index) => {
                    const label = String(item?.name ?? item?.categoryName ?? `Kategoria ${index + 1}`);
                    const rawValue = item?.id ?? item?.categoryId ?? item?.eventsCategoryId ?? `category-${index + 1}`;
                    const apiValue = resolveNumericCategoryId(item?.id, item?.categoryId, item?.eventsCategoryId) || '0';

                    return {
                        value: String(rawValue),
                        label,
                        apiValue,
                    };
                });

                setRankingCategories([
                    { value: '0', label: 'Wszystkie', apiValue: '0' },
                    ...mappedCategories,
                ]);
            })
            .catch((error) => {
                console.error('Błąd pobierania kategorii:', error);
                if (!isMounted) return;
                setRankingCategories([{ value: '0', label: 'Wszystkie', apiValue: '0' }]);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const hasCurrentRankingCategory = rankingCategories.some((item) => String(item.value) === String(rankingCategory));
        if (!hasCurrentRankingCategory) {
            setRankingCategory('0');
        }
    }, [rankingCategories, rankingCategory]);

    useEffect(() => {
        const hasCurrentEventsCategory = rankingCategories.some((item) => String(item.value) === String(eventsCategory));
        if (!hasCurrentEventsCategory) {
            setEventsCategory('0');
        }
    }, [rankingCategories, eventsCategory]);

    useEffect(() => {
        if (activeTab !== 'Rankingi') return;
        fetchRankings();
    }, [activeTab, fetchRankings, refreshTick]);

    useEffect(() => {
        if (activeTab !== 'Rankingi') {
            setRankingInfoModalOpen(false);
        }
    }, [activeTab]);

    useEffect(() => {
        setRankingInfoModalOpen(false);
    }, [rankingVisibleList]);

    useEffect(() => {
        if (!rankingInfoModalOpen) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setRankingInfoModalOpen(false);
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => {
            window.removeEventListener('keydown', handleEscape);
        };
    }, [rankingInfoModalOpen]);

    useEffect(() => {
        const query = buildStatisticsQuery(usersRange, usersYear, usersMonth, usersWeekStartDate, usersWeekEndDate);
        const usersScopeParams = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
        };
        let isMounted = true;

        Promise.allSettled([
            ApiService.getStatisticsSummary(usersScopeParams),
            ApiService.getStatisticsDemographics(usersScopeParams),
            ApiService.getStatisticsTopCities({ ...usersScopeParams, limit: 5 }),
            ApiService.getStatisticsSeries('REGISTERED', query),
            ApiService.getStatisticsSeries('NEW', query),
            ApiService.getStatisticsSeries('ACTIVE', query),
            ApiService.getStatisticsSeries('PACKAGES', query),
            ApiService.getStatisticsSeries('EVENT_PARTICIPATION', query),
            ApiService.getStatisticsSeries('DELETED', query),
            ApiService.getStatisticsSeries('REPORTS', query),
        ])
            .then(([
                summaryResult,
                demographicsResult,
                topCitiesResult,
                registeredResult,
                newResult,
                activeResult,
                packagesResult,
                eventParticipationResult,
                deletedResult,
                reportsResult,
            ]) => {
                if (!isMounted) return;

                const mapSeries = (result) => (
                    result.status === 'fulfilled'
                        ? buildSeriesFromResponse(extractTimeSeriesPayload(result.value), usersRange, {
                            year: usersYear,
                            month: usersMonth,
                            startValue: usersWeekStartDate,
                            endValue: usersWeekEndDate,
                        })
                        : null
                );
                const mapDemography = (result) => {
                    if (result.status !== 'fulfilled') return null;
                    const payload = extractDemographicBreakdownPayload(result.value);
                    if (!payload) return null;

                    return {
                        gender: normalizeDemographicGender(payload?.gender || {}),
                        ages: normalizeDemographicAges(payload?.ageGroups || payload?.ages || {}),
                    };
                };

                setUsersSummaryApi(summaryResult.status === 'fulfilled' ? summaryResult.value : null);
                setUsersDemographicsApi(demographicsResult.status === 'fulfilled' ? demographicsResult.value : null);
                setUsersTopCitiesApi(
                    topCitiesResult.status === 'fulfilled'
                        ? topCitiesResult.value
                        : (summaryResult.status === 'fulfilled' ? (summaryResult.value?.topCities || summaryResult.value?.cities || null) : null)
                );
                setUsersSeriesApi({
                    registered: mapSeries(registeredResult),
                    newUsers: mapSeries(newResult),
                    activeUsers: mapSeries(activeResult),
                    packActivity: mapSeries(packagesResult),
                    confirmedOutings: mapSeries(eventParticipationResult),
                    accountDeletions: mapSeries(deletedResult),
                    reports: mapSeries(reportsResult),
                    demography: {
                        registered: mapDemography(registeredResult),
                        newUsers: mapDemography(newResult),
                        activeUsers: mapDemography(activeResult),
                        packActivity: mapDemography(packagesResult),
                        confirmedOutings: mapDemography(eventParticipationResult),
                        accountDeletions: mapDemography(deletedResult),
                        reports: mapDemography(reportsResult),
                    },
                });
                setPackSeriesApi(mapSeries(packagesResult));
            })
            .catch((error) => {
                console.error('Błąd pobierania statystyk paczek:', error);
                setPackSeriesApi(null);
                setUsersSeriesApi({});
                setUsersSummaryApi(null);
                setUsersDemographicsApi(null);
                setUsersTopCitiesApi(null);
            });

        return () => {
            isMounted = false;
        };
    }, [usersRange, usersYear, usersMonth, usersWeekStartDate, usersWeekEndDate, refreshTick]);

    useEffect(() => {
        const query = buildStatisticsQuery(
            organizersRange,
            organizersYear,
            organizersMonth,
            organizersWeekStartDate,
            organizersWeekEndDate
        );
        const organizersScopeParams = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
        };
        let isMounted = true;

        Promise.allSettled([
            ApiService.getStatisticsB2BSummary(organizersScopeParams),
            ApiService.getStatisticsB2BSeries('TOTAL_ORGANIZERS', query),
            ApiService.getStatisticsB2BSeries('NEW_ORGANIZERS', query),
        ])
            .then(([summaryResult, totalResult, newResult]) => {
                if (!isMounted) return;

                const mapSeries = (result) => (
                    result.status === 'fulfilled'
                        ? buildSeriesFromResponse(
                            extractTimeSeriesPayload(result.value),
                            organizersRange,
                            {
                                year: organizersYear,
                                month: organizersMonth,
                                startValue: organizersWeekStartDate,
                                endValue: organizersWeekEndDate,
                            }
                        )
                        : null
                );

                setOrganizersSummaryApi(summaryResult.status === 'fulfilled' ? summaryResult.value : null);
                setOrganizersSeriesApi({
                    registered: mapSeries(totalResult),
                    newOrganizers: mapSeries(newResult),
                });
            })
            .catch((error) => {
                console.error('BÅ‚Ä…d pobierania statystyk B2B:', error);
                setOrganizersSummaryApi(null);
                setOrganizersSeriesApi({});
            });

        return () => {
            isMounted = false;
        };
    }, [organizersRange, organizersYear, organizersMonth, organizersWeekStartDate, organizersWeekEndDate, refreshTick]);

    useEffect(() => {
        if (activeTab !== 'Wydarzenia') return;

        const status = eventsSummaryMode === 'ACTIVE' ? 'ACTIVE' : 'ALL';
        const summaryQuery = buildStatisticsQuery(
            eventsSummaryMode === 'ACTIVE' ? 'ALL' : eventsRange,
            eventsYear,
            eventsMonth,
            eventsWeekStartDate,
            eventsWeekEndDate
        );
        const selectedCategoryId = resolveCategoryApiValue(eventsCategory);
        const params = {
            dateFrom: summaryQuery.dateFrom,
            dateTo: summaryQuery.dateTo,
            status,
            ...(selectedCategoryId !== '0' && { category: selectedCategoryId }),
        };
        const seriesParams = {
            ...params,
            interval: summaryQuery.interval,
        };
        let isMounted = true;

        Promise.allSettled([
            ApiService.getStatisticsEventsSummary(params),
            ApiService.getStatisticsEventsAverageParticipantsSeries(seriesParams),
        ])
            .then(([summaryResult, avgSeriesResult]) => {
                if (!isMounted) return;

                setEventsSummaryApi(summaryResult.status === 'fulfilled' ? summaryResult.value : null);
                setEventsAvgParticipantsSeriesApi(
                    avgSeriesResult.status === 'fulfilled'
                        ? buildSeriesFromResponse(
                            extractTimeSeriesPayload(avgSeriesResult.value),
                            eventsSummaryMode === 'ACTIVE' ? 'ALL' : eventsRange,
                            {
                                year: eventsYear,
                                month: eventsMonth,
                                startValue: eventsWeekStartDate,
                                endValue: eventsWeekEndDate,
                            }
                        )
                        : null
                );
            })
            .catch((error) => {
                console.error('BÅ‚Ä…d pobierania statystyk wydarzeÅ„:', error);
                setEventsSummaryApi(null);
                setEventsAvgParticipantsSeriesApi(null);
            });

        return () => {
            isMounted = false;
        };
    }, [
        activeTab,
        eventsSummaryMode,
        eventsRange,
        eventsYear,
        eventsMonth,
        eventsWeekStartDate,
        eventsWeekEndDate,
        eventsCategory,
        resolveCategoryApiValue,
        refreshTick,
    ]);

    useEffect(() => {
        if (activeTab !== 'Wydarzenia') return;

        const selectedCategoryId = resolveCategoryApiValue(eventsCategory);
        const query = buildStatisticsQuery(eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate);
        const rankingParams = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
            limit: 50,
            ...(selectedCategoryId !== '0' && { category: selectedCategoryId }),
        };

        Promise.all([
            ApiService.getStatisticsEventsRanking('VIEWS', rankingParams),
            ApiService.getStatisticsEventsRanking('LIKES', rankingParams),
            ApiService.getStatisticsEventsRanking('PACKAGES', rankingParams),
        ])
            .then(([viewsResponse, likesResponse, packsResponse]) => {
                const viewsMapped = mapRankingData(viewsResponse, { limit: null });
                const likesMapped = mapRankingData(likesResponse, { limit: null });
                const packsMapped = mapRankingData(packsResponse, { limit: null });

                const byId = new Map();
                viewsMapped.forEach((item) => {
                    const idKey = normalizeEventIdKey(item.id);
                    if (!idKey) return;
                    byId.set(idKey, {
                        ...item,
                        views: toSafeMetricNumber(item?.views) ?? toSafeMetricNumber(item?.value) ?? 0,
                    });
                });
                likesMapped.forEach((item) => {
                    const idKey = normalizeEventIdKey(item.id);
                    if (!idKey) return;
                    const previous = byId.get(idKey) || item;
                    byId.set(idKey, {
                        ...previous,
                        likes: toSafeMetricNumber(item?.likes) ?? toSafeMetricNumber(item?.value) ?? 0,
                    });
                });
                packsMapped.forEach((item) => {
                    const idKey = normalizeEventIdKey(item.id);
                    if (!idKey) return;
                    const previous = byId.get(idKey) || item;
                    byId.set(idKey, {
                        ...previous,
                        packs: toSafeMetricNumber(item?.packs) ?? toSafeMetricNumber(item?.value) ?? 0,
                    });
                });

                const merged = Array.from(byId.values());
                setEventsTopInterestApi(merged.length ? merged : null);
                setEventsTopPacksApi(packsMapped.length ? packsMapped : null);
            })
            .catch((error) => {
                console.error('Błąd pobierania statystyk top wydarzeń:', error);
                setEventsTopInterestApi(null);
                setEventsTopPacksApi(null);
            });
    }, [activeTab, eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate, eventsCategory, mapRankingData, resolveCategoryApiValue, refreshTick]);

    useEffect(() => {
        setEventsTopInterestExpanded(false);
        setEventsTopPacksExpanded(false);
    }, [eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate, eventsMetric, eventsCategory]);

    useEffect(() => {
        if (!requestedEventContext.isEventView) return;

        dispatch(toggleActiveTab(EVENT_DETAIL_TAB));
        if (requestedEventContext.eventId != null) {
            setSelectedEventStatsId(requestedEventContext.eventId);
        }
    }, [requestedEventContext, dispatch]);

    useEffect(() => {
        const eventFromState = ensureOrganizerNameForEventItem(mapStatsEventToSidebarItem(location.state?.eventFromList));
        if (!eventFromState) return;

        setEventStatsList((previous) => upsertStatsEvent(previous, eventFromState));
        setSelectedEventStats(eventFromState);

        if (!selectedEventStatsId || normalizeEventIdKey(selectedEventStatsId) !== normalizeEventIdKey(eventFromState.id)) {
            setSelectedEventStatsId(eventFromState.id);
        }
    }, [location.state, selectedEventStatsId, ensureOrganizerNameForEventItem]);

    useEffect(() => {
        let isMounted = true;

        const applyList = (rawEvents) => {
            if (!isMounted) return;

            const mapped = (Array.isArray(rawEvents) ? rawEvents : [])
                .map((item, index) => mapStatsEventToSidebarItem(item, index))
                .map((item) => ensureOrganizerNameForEventItem(item))
                .filter(Boolean);

            const deduped = [];
            const seen = new Set();
            mapped.forEach((item) => {
                const key = normalizeEventIdKey(item.id);
                if (!key) return;
                if (seen.has(key)) return;
                seen.add(key);
                deduped.push(item);
            });

            setEventStatsList((previous) => {
                const merged = deduped.reduce((acc, item) => upsertStatsEvent(acc, item), [...previous]);
                return merged;
            });
        };

        setEventStatsListLoading(true);
        setEventStatsListError('');

        ApiService.searchEventsForStatistics({
            query: '',
            page: 0,
            size: 200,
        })
            .then((response) => {
                const data = extractCollectionPayload(response);
                applyList(data);
            })
            .catch((error) => {
                console.error('Błąd pobierania listy wydarzeń do statystyk:', error);
                if (!isMounted) return;
                setEventStatsListError('Nie udało się pobrać listy wydarzeń.');
            })
            .finally(() => {
                if (!isMounted) return;
                setEventStatsListLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [ensureOrganizerNameForEventItem, refreshTick]);

    useEffect(() => {
        const ownersToResolve = Array.from(
            new Set(
                (eventStatsList || [])
                    .map((item) => ({
                        ownerId: resolveOwnerIdFromEventLike(item) || resolveOwnerIdFromEventLike(item?.statsPayload),
                        organizerName: item?.organizerName,
                    }))
                    .filter(({ ownerId, organizerName }) => (
                        Boolean(ownerId)
                        && !hasOrganizerName(organizerName)
                        && !organizerNameByOwnerIdRef.current.has(ownerId)
                        && !organizerNameFetchInFlightRef.current.has(ownerId)
                    ))
                    .map(({ ownerId }) => ownerId)
            )
        );

        if (!ownersToResolve.length) return;

        ownersToResolve.forEach((ownerId) => {
            organizerNameFetchInFlightRef.current.add(ownerId);
            fetchOrganizerCompanyNameByOwnerId(ownerId)
                .then((companyName) => {
                    patchOrganizerNameByOwnerId(ownerId, companyName);
                })
                .finally(() => {
                    organizerNameFetchInFlightRef.current.delete(ownerId);
                });
        });
    }, [eventStatsList, fetchOrganizerCompanyNameByOwnerId, patchOrganizerNameByOwnerId]);

    useEffect(() => {
        if (!eventStatsList.length) return;

        if (selectedEventStatsId != null && eventStatsList.some((item) => normalizeEventIdKey(item.id) === normalizeEventIdKey(selectedEventStatsId))) {
            return;
        }

        if (requestedEventContext.eventId != null) {
            const requestedExists = eventStatsList.some((item) => normalizeEventIdKey(item.id) === normalizeEventIdKey(requestedEventContext.eventId));
            if (requestedExists) {
                setSelectedEventStatsId(requestedEventContext.eventId);
                return;
            }
        }

        setSelectedEventStatsId(eventStatsList[0].id);
    }, [eventStatsList, selectedEventStatsId, requestedEventContext.eventId]);

    useEffect(() => {
        if (selectedEventStatsId == null) {
            setSelectedEventStats(null);
            return;
        }

        const localMatch = eventStatsList.find((item) => normalizeEventIdKey(item.id) === normalizeEventIdKey(selectedEventStatsId));
        if (localMatch) {
            setSelectedEventStats(localMatch);
        }
    }, [selectedEventStatsId, eventStatsList]);

    useEffect(() => {
        if (selectedEventStatsId == null) return;
        if (isUuidLike(selectedEventStatsId)) return;

        let isMounted = true;

        ApiService.getEvent(selectedEventStatsId)
            .then((response) => {
                if (!isMounted) return;

                const mapped = ensureOrganizerNameForEventItem(mapStatsEventToSidebarItem(response?.data || {}));
                if (!mapped) return;

                if (mapped.ownerId && hasOrganizerName(mapped.organizerName)) {
                    organizerNameByOwnerIdRef.current.set(mapped.ownerId, mapped.organizerName);
                }

                setEventStatsList((previous) => upsertStatsEvent(previous, mapped));
                setSelectedEventStats(mapped);
            })
            .catch((error) => {
                console.error(`Błąd pobierania szczegółów wydarzenia ${selectedEventStatsId}:`, error);
            });

        return () => {
            isMounted = false;
        };
    }, [selectedEventStatsId, ensureOrganizerNameForEventItem]);

    useEffect(() => {
        if (activeTab !== EVENT_DETAIL_TAB || selectedEventStatsId == null) {
            return;
        }

        const query = buildStatisticsQuery(eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate);
        const params = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
        };
        let isMounted = true;

        Promise.allSettled([
            ApiService.getStatisticsEventSummary(selectedEventStatsId, params),
            ApiService.getStatisticsEventDemographicsAge(selectedEventStatsId, params),
            ApiService.getStatisticsEventActivitySeries(selectedEventStatsId, {
                ...params,
                interval: query.interval,
            }),
        ])
            .then(([summaryResult, ageResult, activityResult]) => {
                if (!isMounted) return;

                setEventSummaryApi(summaryResult.status === 'fulfilled' ? summaryResult.value : null);

                if (ageResult.status === 'fulfilled') {
                    const ageResponse = ageResult.value || {};
                    const ageGroups = Array.isArray(ageResponse?.ageGroups) ? ageResponse.ageGroups : [];
                    const ageDataByGender = ageResponse?.data && typeof ageResponse.data === 'object'
                        ? ageResponse.data
                        : null;

                    let mappedAge = [];
                    if (ageDataByGender && ageGroups.length) {
                        mappedAge = ageGroups.map((ageRange) => {
                            const key = String(ageRange || '').replace('–', '-');
                            const women = toSafeMetricNumber(ageDataByGender?.K?.[ageRange] ?? ageDataByGender?.K?.[key]) ?? 0;
                            const men = toSafeMetricNumber(ageDataByGender?.M?.[ageRange] ?? ageDataByGender?.M?.[key]) ?? 0;
                            const other = toSafeMetricNumber(ageDataByGender?.I?.[ageRange] ?? ageDataByGender?.I?.[key]) ?? 0;
                            return {
                                range: key,
                                count: clampValue(women + men + other),
                                byGender: {
                                    K: clampValue(women),
                                    M: clampValue(men),
                                    I: clampValue(other),
                                },
                            };
                        });
                    } else {
                        const rawAge = extractCollectionPayload(ageResponse);
                        mappedAge = (Array.isArray(rawAge) ? rawAge : []).map((item) => ({
                            range: String(item?.range || item?.ageRange || item?.label || '').replace('–', '-'),
                            count: clampValue(item?.count ?? item?.value ?? 0),
                            byGender: {
                                K: clampValue(item?.K ?? item?.women ?? 0),
                                M: clampValue(item?.M ?? item?.men ?? 0),
                                I: clampValue(item?.I ?? item?.other ?? 0),
                            },
                        }));
                    }

                    setEventAgeDataApi(mappedAge.length ? mappedAge : null);
                } else {
                    setEventAgeDataApi(null);
                }

                let viewingResult;
                let packResult;

                if (activityResult.status === 'fulfilled') {
                    const rawActivity = extractTimeSeriesPayload(activityResult.value);
                    const mappedActivity = (Array.isArray(rawActivity) ? rawActivity : []).map((item) => ({
                        label: item?.label || item?.date || item?.period || '',
                        viewing: clampValue(item?.viewing ?? item?.views ?? item?.viewsCount ?? 0),
                        packs: clampValue(item?.packs ?? item?.packages ?? item?.packagesCount ?? item?.packagesCreated ?? 0),
                        favorites: clampValue(item?.favorites ?? item?.likes ?? item?.liked ?? item?.favoritesAdded ?? 0),
                    }));
                    const viewingRows = mappedActivity.map((item) => ({ date: item.label, value: item.viewing }));
                    const packRows = mappedActivity.map((item) => ({ date: item.label, value: item.packs }));

                    setEventActivitySeriesApi(mappedActivity.length ? mappedActivity : null);
                    viewingResult = { status: 'fulfilled', value: viewingRows };
                    packResult = { status: 'fulfilled', value: packRows };
                } else {
                    setEventActivitySeriesApi(null);
                    viewingResult = { status: 'rejected', reason: activityResult.reason };
                    packResult = { status: 'rejected', reason: activityResult.reason };
                }

                if (viewingResult.status === 'fulfilled') {
                    const series = buildSeriesFromResponse(viewingResult.value, eventsRange, {
                        year: eventsYear,
                        month: eventsMonth,
                        startValue: eventsWeekStartDate,
                        endValue: eventsWeekEndDate,
                    });
                    setEventViewingSeriesApi(series.length ? series : null);
                } else {
                    console.error('Błąd pobierania statystyk wyświetleń wydarzenia:', viewingResult.reason);
                    setEventViewingSeriesApi(null);
                }

                if (packResult.status === 'fulfilled') {
                    const series = buildSeriesFromResponse(packResult.value, eventsRange, {
                        year: eventsYear,
                        month: eventsMonth,
                        startValue: eventsWeekStartDate,
                        endValue: eventsWeekEndDate,
                    });
                    setEventPackSeriesApi(series.length ? series : null);
                } else {
                    console.error('Błąd pobierania statystyk paczek dla wydarzenia:', packResult.reason);
                    setEventPackSeriesApi(null);
                }
            })
            .catch((error) => {
                console.error('Błąd pobierania statystyk wydarzenia:', error);
                if (!isMounted) return;
                setEventViewingSeriesApi(null);
                setEventPackSeriesApi(null);
                setEventActivitySeriesApi(null);
                setEventSummaryApi(null);
                setEventAgeDataApi(null);
            });

        return () => {
            isMounted = false;
        };
    }, [
        activeTab,
        selectedEventStatsId,
        eventsRange,
        eventsYear,
        eventsMonth,
        eventsWeekStartDate,
        eventsWeekEndDate,
        refreshTick,
    ]);

    useEffect(() => {
        if (activeTab !== EVENT_DETAIL_TAB && eventInfoModalKey) {
            setEventInfoModalKey(null);
        }
    }, [activeTab, eventInfoModalKey]);

    useEffect(() => {
        if (!eventInfoModalKey) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setEventInfoModalKey(null);
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => {
            window.removeEventListener('keydown', handleEscape);
        };
    }, [eventInfoModalKey]);

    const usersRegisteredDemography = useMemo(
        () => usersSeriesApi?.demography?.registered || null,
        [usersSeriesApi]
    );
    const usersRegisteredCount = useMemo(
        () => (
            findMetricValueByKeys(usersDemographicsApi, ['totalProfiles'])
            || findMetricValueByKeys(usersSummaryApi, [
                'totalRegistered',
                'registeredUsers',
                'totalRegisteredUsers',
                'totalRegisteredCount',
            ])
            || 0
        ),
        [usersDemographicsApi, usersSummaryApi]
    );
    const usersGenderShareBase = useMemo(() => {
        if (usersDemographicsApi?.genderDistribution || usersDemographicsApi?.genderTotals) {
            const rows = mapGenderRowsFromDemographics(usersDemographicsApi, usersRegisteredCount);

            return [
                { key: 'K', label: GENDER_LABELS.K, color: GENDER_COLORS.K, percent: rows.K.percent, count: rows.K.count },
                { key: 'M', label: GENDER_LABELS.M, color: GENDER_COLORS.M, percent: rows.M.percent, count: rows.M.count },
                { key: 'I', label: GENDER_LABELS.I, color: GENDER_COLORS.I, percent: rows.I.percent, count: rows.I.count },
            ];
        }

        const resolved = resolveGenderDemography(usersRegisteredDemography?.gender || {});
        return [
            { key: 'K', label: GENDER_LABELS.K, color: GENDER_COLORS.K, percent: resolved.rows.K.percent, count: resolved.rows.K.count },
            { key: 'M', label: GENDER_LABELS.M, color: GENDER_COLORS.M, percent: resolved.rows.M.percent, count: resolved.rows.M.count },
            { key: 'I', label: GENDER_LABELS.I, color: GENDER_COLORS.I, percent: resolved.rows.I.percent, count: resolved.rows.I.count },
        ];
    }, [usersDemographicsApi, usersRegisteredCount, usersRegisteredDemography]);
    const usersGenderShareData = useMemo(
        () => applyGenderFilterToShare(usersGenderShareBase, usersGender),
        [usersGenderShareBase, usersGender]
    );
    const usersGenderRatio = useMemo(
        () => getGenderRatioForFilter(usersGenderShareBase, usersGender),
        [usersGenderShareBase, usersGender]
    );
    const usersGenderTotal = useMemo(() => {
        if (usersGender === 'ALL') {
            return usersGenderShareData.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
        }

        const selected = usersGenderShareBase.find((item) => item.key === usersGender);
        return Number(selected?.count) || 0;
    }, [usersGenderShareData, usersGenderShareBase, usersGender]);

    const usersRegisteredSeriesBase = useMemo(() => usersSeriesApi?.registered || [], [usersSeriesApi]);
    const usersNewSeriesBase = useMemo(() => usersSeriesApi?.newUsers || [], [usersSeriesApi]);
    const usersActiveSeriesBase = useMemo(() => usersSeriesApi?.activeUsers || [], [usersSeriesApi]);
    const usersPackSeriesBase = useMemo(() => usersSeriesApi?.packActivity || packSeriesApi || [], [usersSeriesApi, packSeriesApi]);
    const usersConfirmedSeriesBase = useMemo(() => usersSeriesApi?.confirmedOutings || [], [usersSeriesApi]);
    const usersAccountDeletionsSeriesBase = useMemo(() => usersSeriesApi?.accountDeletions || [], [usersSeriesApi]);
    const usersReportsSeriesBase = useMemo(() => usersSeriesApi?.reports || [], [usersSeriesApi]);
    const usersRegisteredSeries = useMemo(
        () => applyGenderFilterToSeries(usersRegisteredSeriesBase, usersGenderRatio),
        [usersRegisteredSeriesBase, usersGenderRatio]
    );
    const usersNewSeries = useMemo(
        () => applyGenderFilterToSeries(usersNewSeriesBase, usersGenderRatio),
        [usersNewSeriesBase, usersGenderRatio]
    );
    const usersActiveSeries = useMemo(
        () => applyGenderFilterToSeries(usersActiveSeriesBase, usersGenderRatio),
        [usersActiveSeriesBase, usersGenderRatio]
    );
    const usersPackSeries = useMemo(
        () => applyGenderFilterToSeries(usersPackSeriesBase, usersGenderRatio),
        [usersPackSeriesBase, usersGenderRatio]
    );
    const usersConfirmedSeries = useMemo(
        () => usersConfirmedSeriesBase,
        [usersConfirmedSeriesBase]
    );
    const usersAccountDeletionsSeries = useMemo(
        () => usersAccountDeletionsSeriesBase,
        [usersAccountDeletionsSeriesBase]
    );
    const usersReportsSeries = useMemo(
        () => usersReportsSeriesBase,
        [usersReportsSeriesBase]
    );

    const organizersRegisteredSeries = useMemo(() => organizersSeriesApi?.registered || [], [organizersSeriesApi]);
    const organizersNewSeries = useMemo(() => organizersSeriesApi?.newOrganizers || [], [organizersSeriesApi]);
    const organizersZeroSeries = useMemo(
        () => buildSeriesFromResponse([], organizersRange, {
            year: organizersYear,
            month: organizersMonth,
            startValue: organizersWeekStartDate,
            endValue: organizersWeekEndDate,
        }),
        [organizersRange, organizersYear, organizersMonth, organizersWeekStartDate, organizersWeekEndDate]
    );
    const organizersPurchasedPromotionsSeries = useMemo(
        () => (
            organizersSeriesApi?.purchasedPromotions?.length
                ? organizersSeriesApi.purchasedPromotions
                : organizersZeroSeries
        ),
        [organizersSeriesApi, organizersZeroSeries]
    );
    const organizersPromotionsValueSeries = useMemo(
        () => (
            organizersSeriesApi?.promotionsValue?.length
                ? organizersSeriesApi.promotionsValue
                : organizersZeroSeries
        ),
        [organizersSeriesApi, organizersZeroSeries]
    );
    const organizersKpiCards = useMemo(() => {
        return [
            {
                key: 'purchasedPromotions',
                label: 'Liczba zakupionych promocji',
                value: formatStatNumber(
                    b2bPromoSummary?.promotionsPurchasedCount ??
                    getTrendLastValue(organizersPurchasedPromotionsSeries)
                ),
                note: 'w wybranym okresie',
            },
            {
                key: 'promotionsValue',
                label: 'Wartość promocji (zł)',
                value: formatStatNumber(
                    b2bPromoSummary?.promotionsTotalValuePln ??
                    getTrendLastValue(organizersPromotionsValueSeries)
                ),
                note: 'w wybranym okresie',
            },
        ];
    }, [organizersPurchasedPromotionsSeries, organizersPromotionsValueSeries, b2bPromoSummary]);
    const eventsSummaryRangeMode = eventsSummaryMode === 'ACTIVE' ? 'ALL' : eventsRange;
    const eventsSummaryYear = eventsSummaryMode === 'ACTIVE' ? '2026' : eventsYear;
    const eventsSummaryMonth = eventsSummaryMode === 'ACTIVE' ? '01' : eventsMonth;
    const eventsSummaryWeekStartDate = eventsSummaryMode === 'ACTIVE' ? DEFAULT_WEEK_RANGE.start : eventsWeekStartDate;
    const eventsSummaryWeekEndDate = eventsSummaryMode === 'ACTIVE' ? DEFAULT_WEEK_RANGE.end : eventsWeekEndDate;

    const eventsAvgParticipantsSeries = useMemo(() => (
        eventsAvgParticipantsSeriesApi?.length
            ? eventsAvgParticipantsSeriesApi
            : []
    ), [
        eventsAvgParticipantsSeriesApi,
        eventsSummaryRangeMode,
        eventsSummaryYear,
        eventsSummaryMonth,
        eventsSummaryWeekStartDate,
        eventsSummaryWeekEndDate,
    ]);

    const eventsAvgParticipantsCard = useMemo(() => {
        return {
            key: 'avgParticipants',
            label: 'Średnia liczba uczestników',
            value: formatStatNumber(getTrendLastValue(eventsAvgParticipantsSeries)),
            note: eventsSummaryMode === 'ACTIVE' ? 'dla aktywnych wydarzeń' : 'w wybranym okresie',
        };
    }, [eventsAvgParticipantsSeries, eventsSummaryMode]);

    const eventsCategoryFilters = useMemo(
        () => rankingCategories.length
            ? rankingCategories
            : [{ value: '0', label: 'Wszystkie', apiValue: '0' }],
        [rankingCategories]
    );
    const selectedEventsCategory = String(eventsCategory ?? '0');
    const selectedEventsCategoryOption = categoryOptionsByValue.get(selectedEventsCategory);
    const selectedEventsCategoryApiValue = resolveCategoryApiValue(selectedEventsCategory);
    const selectedEventsCategoryLabel = normalizeTextValue(selectedEventsCategoryOption?.label);
    const filterEventByCategory = useCallback((item) => {
        if (selectedEventsCategory === '0') return true;

        const itemNumericCategory = resolveNumericCategoryId(item?.categoryId, item?.eventsCategoryId, item?.category?.id);
        if (selectedEventsCategoryApiValue !== '0' && itemNumericCategory) {
            return itemNumericCategory === selectedEventsCategoryApiValue;
        }

        const itemCategoryLabel = normalizeTextValue(item?.categoryName ?? item?.eventsCategoryName ?? item?.category?.name);
        if (selectedEventsCategoryLabel && itemCategoryLabel) {
            return itemCategoryLabel === selectedEventsCategoryLabel;
        }

        return true;
    }, [selectedEventsCategory, selectedEventsCategoryApiValue, selectedEventsCategoryLabel]);
    const applyCategoryFilterIfPossible = useCallback((items) => {
        if (selectedEventsCategory === '0') return items;

        const hasCategoryInPayload = (items || []).some((item) => {
            const itemNumericCategory = resolveNumericCategoryId(item?.categoryId, item?.eventsCategoryId, item?.category?.id);
            const itemCategoryLabel = normalizeTextValue(item?.categoryName ?? item?.eventsCategoryName ?? item?.category?.name);
            return Boolean(itemNumericCategory || itemCategoryLabel);
        });
        if (!hasCategoryInPayload) {
            return items;
        }

        return (items || []).filter(filterEventByCategory);
    }, [selectedEventsCategory, filterEventByCategory]);
    const eventsInterestSource = useMemo(() => (
        applyCategoryFilterIfPossible(eventsTopInterestApi?.length ? eventsTopInterestApi : [])
    ), [eventsTopInterestApi, applyCategoryFilterIfPossible]);
    const eventsPacksSource = useMemo(() => (
        applyCategoryFilterIfPossible(eventsTopPacksApi?.length ? eventsTopPacksApi : [])
    ), [eventsTopPacksApi, applyCategoryFilterIfPossible]);
    const eventsInterestItems = useMemo(
        () => (
            [...eventsInterestSource]
                .map((item) => ({
                    ...item,
                    metricValue: eventsMetric === 'views'
                        ? (toSafeMetricNumber(item?.views) ?? toSafeMetricNumber(item?.value) ?? 0)
                        : (toSafeMetricNumber(item?.likes) ?? 0),
                }))
                .sort((left, right) => right.metricValue - left.metricValue)
        ),
        [eventsInterestSource, eventsMetric]
    );
    const eventsTopPacksItems = useMemo(
        () => (
            [...eventsPacksSource]
                .map((item) => ({
                    ...item,
                    metricValue: toSafeMetricNumber(item?.packs) ?? toSafeMetricNumber(item?.value) ?? 0,
                }))
                .sort((left, right) => right.metricValue - left.metricValue)
        ),
        [eventsPacksSource]
    );
    const eventsCategoryBreakdownItems = useMemo(() => {
        const apiCategories = extractCollectionPayload(
            eventsSummaryApi?.categoriesBreakdown
            || eventsSummaryApi?.categories
            || eventsSummaryApi?.categoryBreakdown
            || eventsSummaryApi
        );
        if (Array.isArray(apiCategories) && apiCategories.length && apiCategories.some((item) => item?.category || item?.count != null)) {
            return apiCategories.map((item) => ({
                category: item?.category || item?.name || item?.label || 'Kategoria',
                percent: clampValue(item?.percentage ?? item?.percent ?? item?.share ?? 0),
                count: clampValue(item?.count ?? item?.value ?? 0),
            }));
        }

        return [];
    }, [eventsSummaryApi]);

    const eventsTotalEventsCard = useMemo(() => {
        const apiTotal = findMetricValueByKeys(eventsSummaryApi, ['totalEvents', 'eventsCount', 'total']);
        const totalCount = apiTotal != null
            ? apiTotal
            : eventsCategoryBreakdownItems.reduce((sum, item) => sum + (Number(item.count) || 0), 0);

        return {
            key: 'all',
            label: 'Ogółem',
            value: formatStatNumber(totalCount),
            note: eventsSummaryMode === 'ACTIVE' ? 'aktywne wydarzenia' : 'wszystkie wydarzenia',
        };
    }, [eventsSummaryApi, eventsCategoryBreakdownItems, eventsSummaryMode]);

    const ageData = useMemo(() => {
        const fromDemographicsEndpoint = mapAgeRowsFromDemographicsApi(
            usersDemographicsApi,
            usersGender
        );
        if (fromDemographicsEndpoint.length) {
            return fromDemographicsEndpoint;
        }

        const ages = usersRegisteredDemography?.ages || [];
        if (!ages.length) return [];

        return ages.map((item) => {
            const percent = clampValue(item?.percent ?? 0);
            const explicitCount = toSafeMetricNumber(item?.count);
            const estimatedCount = usersGenderTotal > 0 ? clampValue((usersGenderTotal * percent) / 100) : 0;
            const count = explicitCount != null ? clampValue(explicitCount) : estimatedCount;
            return {
                range: String(item?.range || ''),
                percent,
                count,
            };
        });
    }, [usersDemographicsApi, usersGender, usersRegisteredDemography, usersGenderTotal]);
    const cityData = useMemo(
        () => ([
            {
                city: 'Warszawa',
                percent: 100,
                count: clampValue(usersRegisteredCount || usersGenderTotal || 0),
            },
        ]),
        [usersRegisteredCount, usersGenderTotal]
    );
    const usersSummaryByKey = useMemo(
        () => {
            const defaults = [...USERS_SUMMARY, ...USERS_PAIRED_EXTRA_SUMMARY]
                .reduce((acc, item) => ({ ...acc, [item.key]: item }), {});
            if (!usersSummaryApi) return defaults;

            const registeredCount = findMetricValueByKeys(usersSummaryApi, [
                'totalRegistered',
                'registeredUsers',
                'totalRegisteredUsers',
                'totalRegistered',
            ]);
            const newCount = findMetricValueByKeys(usersSummaryApi, ['newUsers', 'newUsersInPeriod']);
            const activeCount = findMetricValueByKeys(usersSummaryApi, ['activeUsers', 'activeUsersInPeriod']);
            const inactiveCount = findMetricValueByKeys(usersSummaryApi, ['unloggedUsers', 'inactiveUsersInPeriod', 'inactiveUsers']);
            const packagesCount = findMetricValueByKeys(usersSummaryApi, ['packageActivity', 'packagesActivityCount', 'packagesCount']);
            const eventParticipationPayload = usersSummaryApi?.eventParticipation || {};
            const confirmedCount = findMetricValueByKeys(
                eventParticipationPayload,
                ['confirmed', 'confirmedCount', 'eventParticipationConfirmedCount']
            );
            const participationTotal = findMetricValueByKeys(
                eventParticipationPayload,
                ['totalDeclared', 'declared', 'eventParticipationDeclaredCount', 'participationDeclaredCount']
            );
            const deletedCount = findMetricValueByKeys(usersSummaryApi, ['deletedAccountsCount', 'deletedUsers']);
            const reportsCount = findMetricValueByKeys(usersSummaryApi, ['reportsCount', 'reports']);
            const resolvedInactive = inactiveCount != null && inactiveCount >= 0
                ? inactiveCount
                : Math.max(0, (registeredCount ?? 0) - (activeCount ?? 0));
            const confirmedRateFromPayload = findMetricValueByKeys(
                eventParticipationPayload,
                ['percentage', 'eventParticipationConfirmedRate']
            );
            const confirmedRate = participationTotal > 0
                ? clampValue(((confirmedCount ?? 0) / participationTotal) * 100)
                : (confirmedRateFromPayload ?? findMetricValueByKeys(usersSummaryApi, ['eventParticipationConfirmedRate']) ?? 0);

            return {
                ...defaults,
                ...(registeredCount != null && { registered: { ...defaults.registered, value: formatStatNumber(registeredCount) } }),
                ...(newCount != null && { new: { ...defaults.new, value: formatStatNumber(newCount) } }),
                ...(activeCount != null && { active: { ...defaults.active, value: formatStatNumber(activeCount) } }),
                ...(resolvedInactive != null && { inactive: { ...defaults.inactive, value: formatStatNumber(resolvedInactive) } }),
                ...(packagesCount != null && { pack: { ...defaults.pack, value: formatStatNumber(packagesCount) } }),
                ...(confirmedRate != null && {
                    confirmed: {
                        ...defaults.confirmed,
                        value: `${clampValue(confirmedRate)}%`,
                        note: participationTotal > 0 ? `${formatStatNumber(confirmedCount || 0)} / ${formatStatNumber(participationTotal)} wyjÅ›Ä‡` : defaults.confirmed.note,
                    },
                }),
                ...(deletedCount != null && { accountDeletion: { ...defaults.accountDeletion, value: formatStatNumber(deletedCount) } }),
                ...(reportsCount != null && { reports: { ...defaults.reports, value: formatStatNumber(reportsCount) } }),
            };
        },
        [usersSummaryApi]
    );
    const organizersSummaryByKey = useMemo(
        () => {
            const defaults = ORGANIZERS_SUMMARY.reduce((acc, item) => ({ ...acc, [item.key]: item }), {});
            if (!organizersSummaryApi) return defaults;

            const totalOrganizers = findMetricValueByKeys(organizersSummaryApi, ['totalOrganizers', 'registeredOrganizers']);
            const newOrganizers = findMetricValueByKeys(organizersSummaryApi, ['newOrganizersInPeriod', 'newOrganizers']);

            return {
                ...defaults,
                ...(totalOrganizers != null && { registered: { ...defaults.registered, value: formatStatNumber(totalOrganizers) } }),
                ...(newOrganizers != null && { new: { ...defaults.new, value: formatStatNumber(newOrganizers) } }),
            };
        },
        [organizersSummaryApi]
    );
    const organizersKpiByKey = useMemo(
        () => organizersKpiCards.reduce((acc, item) => ({ ...acc, [item.key]: item }), {}),
        [organizersKpiCards]
    );
    const selectedAgeBreakdown = useMemo(
        () => usersRegisteredDemography?.ages || [],
        [usersRegisteredDemography]
    );
    const getMetricDemography = useCallback((metricKey) => {
        const metricDemography = usersSeriesApi?.demography?.[metricKey] || null;
        const normalizedGender = normalizeGenderBreakdown(normalizeDemographicGender(metricDemography?.gender || {}));
        const agesBase = metricDemography?.ages || selectedAgeBreakdown || [];
        let filteredGender = normalizedGender;

        if (usersGender !== 'ALL') {
            const selectedEntry = normalizeGenderValue(normalizedGender[usersGender]);
            filteredGender = {
                K: { percent: usersGender === 'K' ? 100 : 0, count: usersGender === 'K' ? selectedEntry.count : 0 },
                M: { percent: usersGender === 'M' ? 100 : 0, count: usersGender === 'M' ? selectedEntry.count : 0 },
                I: { percent: usersGender === 'I' ? 100 : 0, count: usersGender === 'I' ? selectedEntry.count : 0 },
            };
        }

        return {
            gender: scaleGenderBreakdownCounts(filteredGender, 1),
            ages: agesBase,
        };
    }, [usersGender, selectedAgeBreakdown, usersSeriesApi]);
    const usersNewDemography = useMemo(() => getMetricDemography('newUsers'), [getMetricDemography]);
    const usersActiveDemography = useMemo(() => getMetricDemography('activeUsers'), [getMetricDemography]);
    const usersPackDemography = useMemo(() => getMetricDemography('packActivity'), [getMetricDemography]);
    const rankingLikesItems = useMemo(
        () => (
            [...(rankingInterest || [])]
                .sort((left, right) => (toSafeMetricNumber(right?.likes) || 0) - (toSafeMetricNumber(left?.likes) || 0))
                .slice(0, RANKING_LIST_LIMIT)
                .map((item, index) => ({
                    ...item,
                    rank: index + 1,
                }))
        ),
        [rankingInterest]
    );
    const rankingJoinItems = useMemo(
        () => (
            [...(rankingPacks || [])]
                .sort((left, right) => (toSafeMetricNumber(right?.packs) || 0) - (toSafeMetricNumber(left?.packs) || 0))
                .slice(0, RANKING_LIST_LIMIT)
                .map((item, index) => ({
                    ...item,
                    rank: index + 1,
                }))
        ),
        [rankingPacks]
    );
    const rankingListConfig = useMemo(
        () => (
            rankingVisibleList === 'packs'
                ? {
                    keyPrefix: 'packs',
                    titleAccent: 'Top 10',
                    titleSuffix: 'wydarzeń według dołączeń',
                    infoTitle: 'Wydarzenia według dołączeń',
                    infoDescription: 'Utworzenie lub dołączenie do paczki pod wydarzeniem.',
                    items: rankingJoinItems,
                }
                : {
                    keyPrefix: 'interest',
                    titleAccent: 'Top 10',
                    titleSuffix: 'wydarzeń według polubień',
                    infoTitle: 'Wydarzenia według polubień',
                    infoDescription: 'Liczba użytkowników, którzy polubili wydarzenie.',
                    items: rankingLikesItems,
                }
        ),
        [rankingVisibleList, rankingLikesItems, rankingJoinItems]
    );
    const resolveEventImageHash = useCallback((item) => (
        extractEventImageHash(item, true) || extractEventImageHash(item?.statsPayload, true)
    ), []);
    const fetchEventImageByHash = useCallback(async (imageHashCode) => {
        if (!imageHashCode || !accessToken) return null;

        try {
            const imgResponse = await fetch(`/api/events-images/${imageHashCode}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                credentials: 'include',
                mode: 'cors',
            });

            if (!imgResponse.ok) return null;

            const blob = await imgResponse.blob();
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error(`Błąd pobierania obrazka wydarzenia ${imageHashCode}:`, error);
            return null;
        }
    }, [accessToken]);
    const resolveEventImageHashFromApi = useCallback(async (eventId, initialHash = null) => {
        const normalizedInitial = pickRawImageId(initialHash);
        if (normalizedInitial && hasEventImagePrefix(normalizedInitial)) return normalizedInitial;
        const eventIdKey = normalizeEventIdKey(eventId);
        if (!eventIdKey) return normalizedInitial || null;

        if (!isUuidLike(eventIdKey)) {
            try {
                const eventResponse = await ApiService.getEvent(eventIdKey);
                const fromEvent = extractEventImageHash(eventResponse?.data);
                if (fromEvent) return fromEvent;
            } catch (error) {
                console.error(`Blad pobierania eventu ${eventId} dla obrazka:`, error);
            }
        }

        try {
            const imagesResponse = await ApiService.getAllImagesForEvent(eventIdKey);
            const imageIds = Array.isArray(imagesResponse?.data) ? imagesResponse.data : [];
            const firstEventPrefixed = imageIds.find((imageId) => hasEventImagePrefix(imageId));
            if (firstEventPrefixed) return String(firstEventPrefixed);
            const firstImage = pickRawImageId(imageIds[0]);
            if (firstImage) return firstImage;
        } catch (error) {
            console.error(`Blad pobierania listy obrazk�w eventu ${eventId}:`, error);
        }

        return normalizedInitial || null;
    }, []);
    const imageCandidateItems = useMemo(() => {
        const byEventId = new Map();
        const pushCandidate = (item) => {
            const eventIdKey = normalizeEventIdKey(item?.id ?? item?.eventId);
            if (!eventIdKey) return;

            const imageHashCode = resolveEventImageHash(item);
            if (!imageHashCode) return;

            const existing = byEventId.get(eventIdKey);
            if (existing) {
                const existingHasPrefix = hasEventImagePrefix(existing.imageHashCode);
                const nextHasPrefix = hasEventImagePrefix(imageHashCode);
                if (existingHasPrefix || !nextHasPrefix) return;
            }

            byEventId.set(eventIdKey, {
                eventId: eventIdKey,
                imageHashCode,
            });
        };

        (eventStatsList || []).forEach(pushCandidate);
        (rankingLikesItems || []).forEach(pushCandidate);
        (rankingJoinItems || []).forEach(pushCandidate);

        return Array.from(byEventId.values());
    }, [eventStatsList, rankingLikesItems, rankingJoinItems, resolveEventImageHash]);
    useEffect(() => {
        if (!imageCandidateItems.length) return;

        imageCandidateItems.forEach(({ eventId, imageHashCode }) => {
            const previousHash = fetchedImageHashesByEventRef.current.get(eventId);
            const alreadyLoaded = Boolean(eventImageUrlById[eventId]);
            if (previousHash === imageHashCode && alreadyLoaded) return;
            if (imageFetchInFlightByEventRef.current.has(eventId)) return;

            imageFetchInFlightByEventRef.current.add(eventId);

            resolveEventImageHashFromApi(eventId, imageHashCode)
                .then((resolvedHash) => {
                    if (!resolvedHash) return null;
                    return fetchEventImageByHash(resolvedHash)
                        .then((imageObjectUrl) => ({ imageObjectUrl, resolvedHash }));
                })
                .then((imageObjectUrl) => {
                    if (!imageObjectUrl?.imageObjectUrl) return;

                    const previousObjectUrl = objectUrlByEventRef.current.get(eventId);
                    if (previousObjectUrl && previousObjectUrl !== imageObjectUrl.imageObjectUrl) {
                        URL.revokeObjectURL(previousObjectUrl);
                    }

                    fetchedImageHashesByEventRef.current.set(eventId, imageObjectUrl.resolvedHash);
                    objectUrlByEventRef.current.set(eventId, imageObjectUrl.imageObjectUrl);
                    setEventImageUrlById((previous) => ({
                        ...previous,
                        [eventId]: imageObjectUrl.imageObjectUrl,
                    }));
                })
                .finally(() => {
                    imageFetchInFlightByEventRef.current.delete(eventId);
                });
        });
    }, [eventImageUrlById, imageCandidateItems, fetchEventImageByHash, resolveEventImageHashFromApi]);
    useEffect(() => () => {
        objectUrlByEventRef.current.forEach((url) => {
            if (url) URL.revokeObjectURL(url);
        });
        objectUrlByEventRef.current.clear();
        fetchedImageHashesByEventRef.current.clear();
        imageFetchInFlightByEventRef.current.clear();
    }, []);
    const eventStatsFilteredList = useMemo(() => {
        const query = normalizeTextValue(eventStatsSearch);
        if (!query) return eventStatsList;

        return eventStatsList.filter((item) => {
            const name = normalizeTextValue(item?.name);
            const organizer = normalizeTextValue(item?.organizerName);
            return name.includes(query) || organizer.includes(query);
        });
    }, [eventStatsList, eventStatsSearch]);
    const resolveRenderedEventImageUrl = useCallback((item) => {
        const eventIdKey = normalizeEventIdKey(item?.id ?? item?.eventId);
        if (eventIdKey) {
            const resolvedObjectUrl = eventImageUrlById[eventIdKey];
            if (resolvedObjectUrl) return resolvedObjectUrl;
        }
        return '';
    }, [eventImageUrlById]);
    const selectedEventStatsData = useMemo(() => {
        if (selectedEventStats?.id != null && normalizeEventIdKey(selectedEventStats?.id) === normalizeEventIdKey(selectedEventStatsId)) {
            return selectedEventStats;
        }

        return eventStatsList.find((item) => normalizeEventIdKey(item.id) === normalizeEventIdKey(selectedEventStatsId)) || null;
    }, [selectedEventStats, selectedEventStatsId, eventStatsList]);
    const selectedEventLocation = useMemo(
        () => [selectedEventStatsData?.place, selectedEventStatsData?.city].filter(Boolean).join(', '),
        [selectedEventStatsData]
    );
    const selectedEventDate = useMemo(
        () => formatSidebarEventDate(selectedEventStatsData),
        [selectedEventStatsData]
    );
    const eventViewingSeries = useMemo(() => {
        return eventViewingSeriesApi?.length ? eventViewingSeriesApi : [];
    }, [eventViewingSeriesApi]);
    const eventPackSeries = useMemo(() => {
        return eventPackSeriesApi?.length ? eventPackSeriesApi : [];
    }, [eventPackSeriesApi]);
    const eventFavoritesSeries = useMemo(() => {
        if (eventActivitySeriesApi?.length) {
            return eventActivitySeriesApi.map((item) => ({
                label: item.label,
                value: clampValue(item.favorites || 0),
            }));
        }

        return [];
    }, [eventActivitySeriesApi]);
    const eventActivitySeries = useMemo(() => {
        if (eventActivitySeriesApi?.length) {
            return eventActivitySeriesApi;
        }

        const allLabels = Array.from(new Set([
            ...(eventViewingSeries || []).map((item) => item.label),
            ...(eventPackSeries || []).map((item) => item.label),
            ...(eventFavoritesSeries || []).map((item) => item.label),
        ]));
        const viewsByLabel = new Map((eventViewingSeries || []).map((item) => [item.label, Number(item.value) || 0]));
        const packsByLabel = new Map((eventPackSeries || []).map((item) => [item.label, Number(item.value) || 0]));
        const favoritesByLabel = new Map((eventFavoritesSeries || []).map((item) => [item.label, Number(item.value) || 0]));

        return allLabels.map((label) => ({
            label,
            viewing: viewsByLabel.get(label) || 0,
            packs: packsByLabel.get(label) || 0,
            favorites: favoritesByLabel.get(label) || 0,
        }));
    }, [eventActivitySeriesApi, eventViewingSeries, eventPackSeries, eventFavoritesSeries]);
    const eventSummaryViewsGender = useMemo(
        () => eventSummaryApi?.views?.genderBreakdown || null,
        [eventSummaryApi]
    );
    const eventSummaryPacksGender = useMemo(
        () => eventSummaryApi?.packageParticipants?.genderBreakdown || null,
        [eventSummaryApi]
    );
    const eventViewsTotal = useMemo(() => {
        const apiValue = findMetricValueByKeys(eventSummaryApi?.views, ['total']);
        if (apiValue != null) return clampValue(apiValue);
        return eventViewingSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
    }, [eventSummaryApi, eventViewingSeries]);
    const eventPacksTotal = useMemo(() => {
        const apiValue = findMetricValueByKeys(eventSummaryApi?.packageParticipants, ['total']);
        if (apiValue != null) return clampValue(apiValue);
        return eventPackSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
    }, [eventSummaryApi, eventPackSeries]);
    const eventFavoritesTotal = useMemo(
        () => eventFavoritesSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0),
        [eventFavoritesSeries]
    );
    const eventConversionRate = useMemo(() => {
        const apiValue = findMetricValueByKeys(eventSummaryApi?.conversionInsight, ['conversionRatePercentage']);
        if (apiValue != null) return clampValue(apiValue);
        return eventViewsTotal > 0 ? clampValue((eventPacksTotal / eventViewsTotal) * 100) : 0;
    }, [eventSummaryApi, eventViewsTotal, eventPacksTotal]);
    const eventInterestBoost = useMemo(() => {
        const apiValue = findMetricValueByKeys(eventSummaryApi?.conversionInsight, ['categoryAverageComparisonPercentage']);
        if (apiValue != null) return clampValue(apiValue);
        return eventViewsTotal > 0 ? clampValue(((eventFavoritesTotal + eventPacksTotal) / eventViewsTotal) * 100) : 0;
    }, [eventSummaryApi, eventViewsTotal, eventFavoritesTotal, eventPacksTotal]);
    const eventNewUsersCount = useMemo(
        () => {
            const apiValue = findMetricValueByKeys(eventSummaryApi?.visitorTypes?.breakdown, ['NEW', 'new']);
            if (apiValue != null) return clampValue(apiValue);
            const fallbackValue = findMetricValueByKeys(eventSummaryApi, ['newUsersCount', 'newUsers', 'newVisitorsCount']);
            if (fallbackValue != null) return clampValue(fallbackValue);
            return 0;
        },
        [eventSummaryApi]
    );
    const eventReturningUsersCount = useMemo(
        () => {
            const apiValue = findMetricValueByKeys(eventSummaryApi?.visitorTypes?.breakdown, ['RETURNING', 'returning']);
            if (apiValue != null) return clampValue(apiValue);
            const fallbackValue = findMetricValueByKeys(eventSummaryApi, ['returningUsersCount', 'returningUsers']);
            if (fallbackValue != null) return clampValue(fallbackValue);
            return Math.max(0, eventViewsTotal - eventNewUsersCount);
        },
        [eventSummaryApi, eventViewsTotal, eventNewUsersCount]
    );
    const eventViewingLegend = useMemo(() => {
        if (eventSummaryViewsGender) {
            return addPercentToLegendItems([
                { key: 'K', label: 'Kobiety', count: clampValue(eventSummaryViewsGender?.K ?? 0), color: EVENT_GENDER_CARD_COLORS.K },
                { key: 'M', label: 'Mężczyźni', count: clampValue(eventSummaryViewsGender?.M ?? 0), color: EVENT_GENDER_CARD_COLORS.M },
                { key: 'I', label: 'Inna', count: clampValue(eventSummaryViewsGender?.I ?? 0), color: EVENT_GENDER_CARD_COLORS.I },
            ]);
        }
        const [womenCount, menCount, otherCount] = buildCountsFromRatios(eventViewsTotal, [0.34, 0.46, 0.20]);
        return addPercentToLegendItems([
            { key: 'K', label: 'Kobiety', count: womenCount, color: EVENT_GENDER_CARD_COLORS.K },
            { key: 'M', label: 'Mężczyźni', count: menCount, color: EVENT_GENDER_CARD_COLORS.M },
            { key: 'I', label: 'Inna', count: otherCount, color: EVENT_GENDER_CARD_COLORS.I },
        ]);
    }, [eventSummaryViewsGender, eventViewsTotal]);
    const eventPacksLegend = useMemo(() => {
        if (eventSummaryPacksGender) {
            return addPercentToLegendItems([
                { key: 'K', label: 'Kobiety', count: clampValue(eventSummaryPacksGender?.K ?? 0), color: EVENT_GENDER_CARD_COLORS.K },
                { key: 'M', label: 'Mężczyźni', count: clampValue(eventSummaryPacksGender?.M ?? 0), color: EVENT_GENDER_CARD_COLORS.M },
                { key: 'I', label: 'Inna', count: clampValue(eventSummaryPacksGender?.I ?? 0), color: EVENT_GENDER_CARD_COLORS.I },
            ]);
        }
        const [womenCount, menCount, otherCount] = buildCountsFromRatios(eventPacksTotal, [0.22, 0.53, 0.25]);
        return addPercentToLegendItems([
            { key: 'K', label: 'Kobiety', count: womenCount, color: EVENT_GENDER_CARD_COLORS.K },
            { key: 'M', label: 'Mężczyźni', count: menCount, color: EVENT_GENDER_CARD_COLORS.M },
            { key: 'I', label: 'Inna', count: otherCount, color: EVENT_GENDER_CARD_COLORS.I },
        ]);
    }, [eventSummaryPacksGender, eventPacksTotal]);
    const eventReturningLegend = useMemo(() => (
        addPercentToLegendItems([
            { key: 'N', label: 'Nowi', count: eventNewUsersCount, color: EVENT_GENDER_CARD_COLORS.K },
            { key: 'R', label: 'Powracający', count: eventReturningUsersCount, color: EVENT_GENDER_CARD_COLORS.M },
        ])
    ), [eventNewUsersCount, eventReturningUsersCount]);
    const eventAgeScale = useMemo(() => 1, []);
    const eventDetailScopeValue = eventsRange === 'ALL' ? 'ALL' : 'WEEK';
    const eventAgeData = useMemo(() => {
        if (eventAgeDataApi?.length) {
            const resolved = eventAgeDataApi.map((item) => {
                const totalCount = clampValue(item.count || 0);
                const selectedCount = eventAgeFilter === 'ALL'
                    ? totalCount
                    : clampValue(item?.byGender?.[eventAgeFilter] || 0);
                return {
                    ...item,
                    range: String(item.range || '').replace('–', '-'),
                    count: selectedCount,
                    percent: 0,
                };
            });

            const totalSelected = resolved.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
            return resolved.map((item) => ({
                ...item,
                percent: totalSelected > 0 ? clampValue((item.count / totalSelected) * 100) : 0,
            }));
        }

        return [];
    }, [eventAgeDataApi, eventAgeFilter]);
    const eventAgeAxisMax = useMemo(() => {
        const maxValue = Math.max(...eventAgeData.map((item) => Number(item.count) || 0), 0);
        if (!maxValue) return 80;
        return Math.max(80, Math.ceil((maxValue + 5) / 20) * 20);
    }, [eventAgeData]);

    return (
        <div>
            <Menu />
            <Content>
                <div className="stats-container">
                    <div className="stats-header">
                        <h1>Statystyki</h1>
                    </div>

                    <div className="stats-content">
                        <div className="stats-sidebar">
                            <div
                                className={`sidebar-item ${activeTab === 'Użytkownicy' ? 'active' : ''}`}
                                onClick={() => handleSidebarClick('Użytkownicy')}
                            >
                                Użytkownicy<span className="right-oriented">&#10217;</span>
                            </div>
                            <div
                                className={`sidebar-item ${activeTab === 'Organizatorzy' ? 'active' : ''}`}
                                onClick={() => handleSidebarClick('Organizatorzy')}
                            >
                                Organizatorzy<span className="right-oriented">&#10217;</span>
                            </div>
                            <div
                                className={`sidebar-item ${activeTab === 'Wydarzenia' ? 'active' : ''}`}
                                onClick={() => handleSidebarClick('Wydarzenia')}
                            >
                                Wydarzenia<span className="right-oriented">&#10217;</span>
                            </div>
                            <div
                                className={`sidebar-item ${activeTab === 'Rankingi' ? 'active' : ''}`}
                                onClick={() => handleSidebarClick('Rankingi')}
                            >
                                Rankingi wydarzeń<span className="right-oriented">&#10217;</span>
                            </div>
                            <div
                                className={`sidebar-item ${activeTab === EVENT_DETAIL_TAB ? 'active' : ''}`}
                                onClick={() => handleSidebarClick(EVENT_DETAIL_TAB)}
                            >
                                Statystyki wydarzenia<span className="right-oriented">&#10217;</span>
                            </div>

                            {activeTab === EVENT_DETAIL_TAB && (
                                <div className="stats-sidebar-events">
                                    <div className="stats-sidebar-events-title">Wydarzenia</div>
                                    <input
                                        type="text"
                                        value={eventStatsSearch}
                                        onChange={(event) => setEventStatsSearch(event.target.value)}
                                        className="stats-sidebar-events-search"
                                        placeholder="Szukaj po nazwie lub organizatorze"
                                    />
                                    <div className="stats-sidebar-events-list">
                                        {eventStatsListLoading && (
                                            <div className="stats-sidebar-events-empty">Ładowanie listy wydarzeń...</div>
                                        )}

                                        {!eventStatsListLoading && eventStatsListError && (
                                            <div className="stats-sidebar-events-empty">{eventStatsListError}</div>
                                        )}

                                        {!eventStatsListLoading && !eventStatsListError && eventStatsFilteredList.length === 0 && (
                                            <div className="stats-sidebar-events-empty">Brak wyników.</div>
                                        )}

                                        {!eventStatsListLoading && !eventStatsListError && eventStatsFilteredList.map((eventItem) => (
                                            <button
                                                key={`stats-sidebar-event-${eventItem.id}`}
                                                type="button"
                                                className={`stats-sidebar-event-item ${normalizeEventIdKey(selectedEventStatsId) === normalizeEventIdKey(eventItem.id) ? 'is-active' : ''}`}
                                                onClick={() => handleSelectEventStats(eventItem)}
                                            >
                                                <div className="stats-sidebar-event-thumb">
                                                    {resolveRenderedEventImageUrl(eventItem) ? (
                                                        <img src={resolveRenderedEventImageUrl(eventItem)} alt={eventItem.name} />
                                                    ) : (
                                                        <span>Brak obrazka</span>
                                                    )}
                                                </div>
                                                <div className="stats-sidebar-event-content">
                                                    <div className="stats-sidebar-event-name">{eventItem.name}</div>
                                                    <div className="stats-sidebar-event-organizer">
                                                        {eventItem.organizerName || [eventItem.place, eventItem.city].filter(Boolean).join(', ') || ORGANIZER_NAME_FALLBACK}
                                                    </div>
                                                    <div className="stats-sidebar-event-meta">
                                                        {formatSidebarEventDateLabel(eventItem)}
                                                    </div>
                                                    <div className="stats-sidebar-event-time">{formatSidebarEventTimeLabel(eventItem)}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {activeTab === 'Użytkownicy' && (
                            <UsersStatsSection
                                renderRangeFilters={renderRangeFilters}
                                usersRange={usersRange}
                                setUsersRange={setUsersRange}
                                usersYear={usersYear}
                                setUsersYear={setUsersYear}
                                usersMonth={usersMonth}
                                setUsersMonth={setUsersMonth}
                                usersWeekStartDate={usersWeekStartDate}
                                setUsersWeekStartDate={setUsersWeekStartDate}
                                usersWeekEndDate={usersWeekEndDate}
                                setUsersWeekEndDate={setUsersWeekEndDate}
                                usersSummaryByKey={usersSummaryByKey}
                                usersGender={usersGender}
                                setUsersGender={setUsersGender}
                                usersGenderShareData={usersGenderShareData}
                                usersGenderTotal={usersGenderTotal}
                                ageData={ageData}
                                cityData={cityData}
                                usersRegisteredSeries={usersRegisteredSeries}
                                usersNewSeries={usersNewSeries}
                                usersActiveSeries={usersActiveSeries}
                                usersPackSeries={usersPackSeries}
                                usersConfirmedSeries={usersConfirmedSeries}
                                usersAccountDeletionsSeries={usersAccountDeletionsSeries}
                                usersReportsSeries={usersReportsSeries}
                                usersNewDemography={usersNewDemography}
                                usersActiveDemography={usersActiveDemography}
                                usersPackDemography={usersPackDemography}
                            />
                        )}

                        {activeTab === 'Organizatorzy' && (
                            <OrganizersStatsSection
                                renderRangeFilters={renderRangeFilters}
                                organizersRange={organizersRange}
                                setOrganizersRange={setOrganizersRange}
                                organizersYear={organizersYear}
                                setOrganizersYear={setOrganizersYear}
                                organizersMonth={organizersMonth}
                                setOrganizersMonth={setOrganizersMonth}
                                organizersWeekStartDate={organizersWeekStartDate}
                                setOrganizersWeekStartDate={setOrganizersWeekStartDate}
                                organizersWeekEndDate={organizersWeekEndDate}
                                setOrganizersWeekEndDate={setOrganizersWeekEndDate}
                                organizersSummaryByKey={organizersSummaryByKey}
                                organizersKpiCards={organizersKpiCards}
                                organizersRegisteredSeries={organizersRegisteredSeries}
                                organizersNewSeries={organizersNewSeries}
                                organizersKpiByKey={organizersKpiByKey}
                                organizersPurchasedPromotionsSeries={organizersPurchasedPromotionsSeries}
                                organizersPromotionsValueSeries={organizersPromotionsValueSeries}
                            />
                        )}

                        {activeTab === 'Wydarzenia' && (
                            <EventsStatsSection
                                eventsSummaryMode={eventsSummaryMode}
                                setEventsSummaryMode={setEventsSummaryMode}
                                renderRangeFilters={renderRangeFilters}
                                eventsRange={eventsRange}
                                setEventsRange={setEventsRange}
                                eventsYear={eventsYear}
                                setEventsYear={setEventsYear}
                                eventsMonth={eventsMonth}
                                setEventsMonth={setEventsMonth}
                                eventsWeekStartDate={eventsWeekStartDate}
                                setEventsWeekStartDate={setEventsWeekStartDate}
                                eventsWeekEndDate={eventsWeekEndDate}
                                setEventsWeekEndDate={setEventsWeekEndDate}
                                eventsTotalEventsCard={eventsTotalEventsCard}
                                eventsCategoryBreakdownItems={eventsCategoryBreakdownItems}
                                eventsAvgParticipantsCard={eventsAvgParticipantsCard}
                                eventsAvgParticipantsSeries={eventsAvgParticipantsSeries}
                                eventsMetric={eventsMetric}
                                setEventsMetric={setEventsMetric}
                                eventsCategoryFilters={eventsCategoryFilters}
                                selectedEventsCategory={selectedEventsCategory}
                                setEventsCategory={setEventsCategory}
                                eventsInterestItems={eventsInterestItems}
                                eventsTopInterestExpanded={eventsTopInterestExpanded}
                                setEventsTopInterestExpanded={setEventsTopInterestExpanded}
                                handleOpenEventFromStats={handleOpenEventFromStats}
                                toSafeMetricNumber={toSafeMetricNumber}
                                eventsTopPacksItems={eventsTopPacksItems}
                                eventsTopPacksExpanded={eventsTopPacksExpanded}
                                setEventsTopPacksExpanded={setEventsTopPacksExpanded}
                            />
                        )}

                        {activeTab === EVENT_DETAIL_TAB && (
                            <EventDetailsStatsSection
                                eventDetailScopeValue={eventDetailScopeValue}
                                handleEventTimeScopeChange={handleEventTimeScopeChange}
                                selectedEventStatsData={selectedEventStatsData}
                                selectedEventLocation={selectedEventLocation}
                                selectedEventDate={selectedEventDate}
                                eventViewingLegend={eventViewingLegend}
                                eventPacksLegend={eventPacksLegend}
                                eventReturningLegend={eventReturningLegend}
                                setEventInfoModalKey={setEventInfoModalKey}
                                eventInterestBoost={eventInterestBoost}
                                eventViewsTotal={eventViewsTotal}
                                eventPacksTotal={eventPacksTotal}
                                eventConversionRate={eventConversionRate}
                                eventAgeFilter={eventAgeFilter}
                                setEventAgeFilter={setEventAgeFilter}
                                eventAgeData={eventAgeData}
                                eventAgeAxisMax={eventAgeAxisMax}
                                eventVisibleSeries={eventVisibleSeries}
                                handleEventVisibleSeriesToggle={handleEventVisibleSeriesToggle}
                                eventActivitySeries={eventActivitySeries}
                                eventInfoModalContent={eventInfoModalContent}
                                eventInfoModalKey={eventInfoModalKey}
                            />
                        )}

                        {activeTab === 'Rankingi' && (
                            <RankingsStatsSection
                                rankingVisibleList={rankingVisibleList}
                                setRankingVisibleList={setRankingVisibleList}
                                rankingCategories={rankingCategories}
                                rankingCategory={rankingCategory}
                                setRankingCategory={setRankingCategory}
                                rankingRange={rankingRange}
                                setRankingRange={setRankingRange}
                                rankingListConfig={rankingListConfig}
                                setRankingInfoModalOpen={setRankingInfoModalOpen}
                                rankingInfoModalOpen={rankingInfoModalOpen}
                                resolveRenderedEventImageUrl={resolveRenderedEventImageUrl}
                            />
                        )}
                    </div>
                </div>
            </Content>
        </div>
    );
}

export default Stats;
